import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLatest } from "@/hooks/useLatest";
import React from "react";
import { api } from "@/lib/api";
import i18n from "@/i18n";
import { translateDetail, translateErrorText } from "@/lib/apiErrorMessage";
import { formatDateShort, formatDateLong } from "@/lib/formatDate";
import { useApp } from "@/context/AppContext";
import { useRestaurantScope } from "@/hooks/useRestaurantScope";
import { useResolvedConfig } from "@/context/ResolvedConfigContext";
import PageHeader from "@/components/PageHeader";
import InventoryExportPanel from "@/components/InventoryExportPanel";
import StatusBadge from "@/components/StatusBadge";
import {
    Plus, Search, ScanLine, Check, Trash2, Boxes, ArrowLeft, X, Camera, Smartphone,
} from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";
import BarcodeScanner from "@/components/BarcodeScanner";
import ProductSuggestionDialog from "@/components/ProductSuggestionDialog";
import PhoneCompanionModal from "@/components/PhoneCompanionModal";
import { MissingPricesReview, InventoryCompletionSummary } from "@/components/MissingPricesReview";

// Message 460 — canonical category ordering for the Physical Inventory
// counted-items list. Uncategorised / non-canonical rows fall into
// "Needs Review" and render AFTER the seven official categories.
const CANONICAL_CATEGORY_ORDER = [
    "Vegetables & Fruit",
    "Meat & Poultry",
    "Dairy",
    "Bakery & Pastry",
    "Dry",
    "Sodas",
    "Alcoholic Beverages",
];
const NEEDS_REVIEW_LABEL = "Needs Review";
// Built-in category names are canonical stored values — translate at display only.
const CAT_KEY = { "Vegetables & Fruit": "vegetablesFruit", "Meat & Poultry": "meatPoultry", Dairy: "dairy",
    "Bakery & Pastry": "bakeryPastry", Dry: "dry", Sodas: "sodas", "Alcoholic Beverages": "alcoholicBeverages",
    "Needs Review": "needsReview" };
const catLabel = (c) => (CAT_KEY[c] ? i18n.t(`categories.${CAT_KEY[c]}`, c) : c);

// Utility — smooth-scroll a counted row into view and pulse-highlight
// it for ~1.8s so the chef sees which existing row they just tried to
// re-count. Message 460 · Section 2.
const highlightCountedRow = (itemId) => {
    if (!itemId) return;
    setTimeout(() => {
        const el = document.querySelector(`[data-item-id="${itemId}"]`);
        if (el?.scrollIntoView) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        if (el?.classList) {
            el.classList.add("bg-amber-100", "ring-2", "ring-amber-400");
            setTimeout(() => {
                el.classList.remove("bg-amber-100", "ring-2", "ring-amber-400");
            }, 1800);
        }
    }, 60);
};

export default function Inventory() {
    const { t } = useTranslation();
    const { user, restaurantId } = useApp();
    // A restaurant-scoped user (legacy "restaurant" role) always operates
    // on their own restaurant — the backend resolves it from the JWT and
    // ignores any restaurant_id we might send. Every other role (corporate,
    // restaurant_admin, head_chef, kitchen_staff, read_only, super_admin
    // acting on a company) needs an EXPLICIT restaurant_id in every
    // restaurant-scoped request. Missing that → HTTP 400
    // "restaurant_id is required for corporate users".
    const {
        needsExplicit: needsExplicitRestaurant,
        scopedParams: params,
        withRestaurant,
        requireSelection,
    } = useRestaurantScope();

    const [counts, setCounts] = useState([]);
    const [activeCount, setActiveCount] = useState(null); // count being edited
    const [startOpen, setStartOpen] = useState(false);
    const [productsDialogOpen, setProductsDialogOpen] = useState(false);
    const [historyDetail, setHistoryDetail] = useState(null);
    const [correctingCountId, setCorrectingCountId] = useState(null);

    const load = useCallback(async () => {
        if (needsExplicitRestaurant && !restaurantId) return;
        const { data: c } = await api.get("/inventory-counts", { params });
        setCounts(c);
        // If there's a draft/in-progress, surface it as the active count
        const open = c.find((x) => x.status === "draft" || x.status === "in_progress");
        if (open) {
            const { data: detail } = await api.get(`/inventory-counts/${open.id}`);
            setActiveCount(detail);
        } else {
            setActiveCount(null);
        }
    }, [restaurantId, needsExplicitRestaurant, params]);
    useEffect(() => { load(); }, [load]);

    const openHistoryDetail = async (countId) => {
        // Message 405 — pull the authoritative accounting dataset AND
        // the count-detail together. The modal MUST NOT display the
        // legacy `count.total_value` scalar (which was the source of
        // the €0.03 drift vs Reports/XLSX). NET/VAT/GROSS come from
        // /reports endpoint so ALL surfaces reconcile to the cent.
        const { data } = await api.get(`/inventory-counts/${countId}`);
        try {
            const { data: acc } = await api.get(
                `/inventory-counts/${countId}/report`, { params: { accounting: true } });
            data._accounting = acc?.meta || null;
        } catch (e) {
            data._accounting = null;
        }
        setHistoryDetail(data);
    };

    const completedCounts = counts.filter((c) => c.status === "completed");

    if (activeCount) {
        return (
            <ActiveCountScreen
                count={activeCount}
                onBack={async () => { await load(); }}
                refresh={async () => {
                    const { data } = await api.get(`/inventory-counts/${activeCount.id}`);
                    setActiveCount(data);
                }}
                onComplete={async () => {
                    await load();
                }}
            />
        );
    }

    return (
        <div className="px-6 lg:px-12 py-10 max-w-6xl mx-auto">
            <PageHeader
                title={t("inventory.inventory", "Inventory")}
                subtitle={t("inventory.whatDoWeCurrentlyHave", "What do we currently have?")}
                stackOnMobile
                action={
                    counts.length > 0 && (
                        <div data-testid="inventory-header-actions" className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
                            <button
                                data-testid="manage-products-button"
                                onClick={() => setProductsDialogOpen(true)}
                                className="h-10 px-4 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-neutral-200 text-sm font-semibold hover:bg-neutral-50"
                            >
                                <Boxes className="h-4 w-4" /> {t("inventory.products", "Products")}
                            </button>
                            <button
                                data-testid="start-inventory-button"
                                onClick={() => setStartOpen(true)}
                                className="h-10 px-4 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800"
                            >
                                <Plus className="h-4 w-4" /> {t("inventory.newCount", "New Count")}
                            </button>
                        </div>
                    )
                }
            />

            {counts.length === 0 ? (
                <div className="kop-card p-12 text-center" data-testid="inventory-empty">
                    <div className="mx-auto h-14 w-14 grid place-items-center rounded-full bg-neutral-100">
                        <Boxes className="h-6 w-6 text-neutral-500" />
                    </div>
                    <div className="mt-5 font-display text-2xl font-extrabold">{t("inventory.noInventoryRecordsYet", "No inventory records yet.")}</div>
                    <p className="mt-2 text-sm text-neutral-500 max-w-sm mx-auto">
                        {t("inventory.startYourFirstInventoryCount", "Start your first inventory count — scan a barcode or search a product to begin.")}
                    </p>
                    <button
                        data-testid="start-first-inventory"
                        onClick={() => setStartOpen(true)}
                        className="mt-6 h-11 px-5 inline-flex items-center gap-2 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800"
                    >
                        <Plus className="h-4 w-4" /> {t("inventory.startFirstInventory", "Start First Inventory")}
                    </button>
                </div>
            ) : (
                <section>
                    {completedCounts.length > 0 && <InventoryExportPanel counts={completedCounts} scopeParams={params} />}
                    <h2 className="font-display text-xl font-bold mb-3">{t("inventory.inventoryHistory", "Inventory history")}</h2>
                    {completedCounts.length === 0 ? (
                        <div className="kop-card p-6 text-sm text-neutral-500">
                            {t("inventory.noCompletedCountsYetUse", "No completed counts yet. Use “New Count” to begin.")}
                        </div>
                    ) : (
                        <div className="kop-card overflow-hidden">
                            <table className="w-full text-sm" data-testid="inventory-history-table">
                                <thead className="bg-neutral-50">
                                    <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                                        <th className="px-5 py-3 font-semibold">{t("inventory.date", "Date")}</th>
                                        <th className="px-5 py-3 font-semibold">{t("inventory.type", "Type")}</th>
                                        <th className="px-5 py-3 font-semibold">{t("inventory.status", "Status")}</th>
                                        <th className="px-5 py-3 font-semibold">{t("inventory.totalValue", "Total value")}</th>
                                        <th className="px-5 py-3 font-semibold">{t("inventory.completedBy", "Completed by")}</th>
                                        <th className="px-5 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-neutral-100">
                                    {completedCounts.map((c) => (
                                        <tr key={c.id} data-testid={`count-row-${c.id}`}>
                                            <td className="px-5 py-3 font-medium">
                                                {formatDateShort(c.count_date)}
                                            </td>
                                            <td className="px-5 py-3 text-neutral-600 capitalize">
                                                {t(`inventory.countTypeShort.${c.count_type || "monthly"}`, (c.count_type || "monthly").replace("_", " "))}
                                            </td>
                                            <td className="px-5 py-3">
                                                <StatusBadge status="completed">{t(`inventory.countStatus.${c.status}`, c.status)}</StatusBadge>
                                            </td>
                                            <td className="px-5 py-3">€ {Number(c.total_value || 0).toFixed(2)}</td>
                                            <td className="px-5 py-3 text-neutral-600">{c.completed_by || "—"}</td>
                                            <td className="px-5 py-3 text-right space-x-2">
                                                <button data-testid={`open-count-${c.id}`}
                                                    onClick={() => openHistoryDetail(c.id)}
                                                    className="h-9 px-3 rounded-md border border-neutral-200 text-xs font-semibold hover:bg-neutral-50">
                                                    {t("inventory.view", "View")}
                                                </button>
                                                <button data-testid={`correct-count-${c.id}`}
                                                    onClick={() => setCorrectingCountId(c.id)}
                                                    className="h-9 px-3 rounded-md border border-amber-300 bg-amber-50 text-xs font-semibold text-amber-800 hover:bg-amber-100">
                                                    {t("inventory.correctCount", "Correct count")}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            )}

            <StartCountDialog
                open={startOpen}
                onClose={() => setStartOpen(false)}
                onStarted={async () => { setStartOpen(false); await load(); }}
            />

            <ProductsDialog
                open={productsDialogOpen}
                onClose={() => setProductsDialogOpen(false)}
            />

            <HistoryDetailDialog
                open={!!historyDetail}
                onClose={() => setHistoryDetail(null)}
                count={historyDetail}
            />
            <CorrectCountDialog
                countId={correctingCountId}
                onClose={() => setCorrectingCountId(null)}
                onSaved={async () => { setCorrectingCountId(null); await load(); }}
            />
        </div>
    );
}

function CorrectCountDialog({ countId, onClose, onSaved }) {
    const { t } = useTranslation();
    const [count, setCount] = useState(null);
    const [items, setItems] = useState([]);
    const [cats, setCats] = useState([]);
    const [catsLoadFailed, setCatsLoadFailed] = useState(false);
    const [patches, setPatches] = useState({});
    const [deletes, setDeletes] = useState(new Set());
    const [adds, setAdds] = useState([]);
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [baselineHint, setBaselineHint] = useState(null);
    const open = !!countId;
    useEffect(() => {
        if (!open) return;
        (async () => {
            setError(""); setPatches({}); setDeletes(new Set()); setAdds([]);
            setReason(""); setHistory([]); setShowHistory(false);
            try {
                const { data } = await api.get(`/inventory-counts/${countId}`);
                setCount(data);
                setItems(data.items || []);
                try {
                    const { data: pc } = await api.get("/categories",
                        { params: { type: "product", include_builtin: 1 } });
                    const names = (Array.isArray(pc) ? pc : (pc?.categories || []))
                        .map((x) => (typeof x === "string" ? x : x?.name))
                        .filter((n) => typeof n === "string" && n.length > 0);
                    setCats(names);
                    setCatsLoadFailed(false);
                } catch { setCats([]); setCatsLoadFailed(true); }
                try {
                    const { data: all } = await api.get("/inventory-counts");
                    const latest = (all || []).filter((c) => c.status === "completed")
                        .sort((a, b) => (b.count_date || "").localeCompare(a.count_date || ""))[0];
                    setBaselineHint(latest && latest.id === countId
                        ? t("inventory.thisIsTheActiveStock", "This is the active stock baseline. Changes will update today's on-hand values.")
                        : null);
                } catch { setBaselineHint(null); }
            } catch (e) {
                setError(t("inventory2.couldNotLoadCount", "Could not load count."));
            }
        })();
    }, [countId, open]);
    const patch = (id, field, val) => {
        setPatches((p) => ({ ...p, [id]: { ...(p[id] || {}), [field]: val } }));
    };
    const toggleDelete = (id) => {
        setDeletes((d) => { const n = new Set(d); n.has(id) ? n.delete(id) : n.add(id); return n; });
    };
    const addNew = () => setAdds((a) => [...a, {
        _k: Math.random().toString(36).slice(2),
        product_name: "", quantity: "", unit: "kpl", price: "", missing_price: false,
        category_override: "", vat_override: "",
    }]);
    const patchAdd = (k, field, val) => {
        setAdds((a) => a.map((r) => r._k === k ? { ...r, [field]: val } : r));
    };
    const removeAdd = (k) => setAdds((a) => a.filter((r) => r._k !== k));
    const loadHistory = async () => {
        try {
            const { data } = await api.get(`/inventory-counts/${countId}/corrections`);
            setHistory(data?.corrections || []);
            setShowHistory(true);
        } catch (e) { setError(t("inventory2.couldNotLoadHistory", "Could not load history.")); }
    };
    const save = async () => {
        if (!reason.trim()) { setError(t("inventory2.reasonIsRequired", "Reason is required.")); return; }
        setSaving(true); setError("");
        const item_patches = Object.entries(patches).map(([item_id, upd]) => {
            const out = { item_id };
            Object.entries(upd).forEach(([k, v]) => {
                if (k === "vat_override" || k === "category_override") {
                    out[k] = v === "" ? null : (k === "vat_override" ? Number(v) : v);
                } else if (k === "quantity" || k === "price") {
                    out[k] = v === "" ? 0 : Number(v);
                } else {
                    out[k] = v;
                }
            });
            return out;
        }).filter((p) => Object.keys(p).length > 1);
        const item_ops = [];
        for (const id of deletes) item_ops.push({ op: "delete", item_id: id });
        for (const r of adds) {
            if (!r.product_name.trim()) continue;
            const op = {
                op: "add",
                product_name: r.product_name.trim(),
                quantity: Number(r.quantity || 0),
                unit: r.unit || "kpl",
            };
            if (r.missing_price) op.missing_price = true;
            else if (r.price !== "") op.price = Number(r.price);
            if (r.category_override) op.category_override = r.category_override;
            if (r.vat_override !== "") op.vat_override = Number(r.vat_override);
            item_ops.push(op);
        }
        try {
            await api.post(`/inventory-counts/${countId}/corrections`, {
                reason: reason.trim(),
                revision: Number(count?.correction_revision || 0),
                item_patches,
                item_ops,
            });
            onSaved();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (e?.response?.status === 409) {
                setError(t("inventory2.thisCountWasCorrectedBy", "This count was corrected by someone else. Reload to continue."));
            } else {
                setError(typeof detail === "string" ? detail
                    : (translateErrorText(detail?.message) || t("inventory.saveFailed", "Save failed.")));
            }
        } finally { setSaving(false); }
    };
    if (!open) return null;
    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-5xl" data-testid="correct-count-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl font-bold">
                        {t("inventory.correctCount", "Correct count")}
                    </DialogTitle>
                    <DialogDescription>
                        {count ? `${formatDateLong(count.count_date)} · ${t("inventory.completedLower", "completed")} · ${count.inventory_month || ""}` : t("inventory.loading", "Loading…")}
                    </DialogDescription>
                </DialogHeader>
                {baselineHint && (
                    <div data-testid="baseline-impact-banner"
                        className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        {baselineHint}
                    </div>
                )}
                {catsLoadFailed && (
                    <div data-testid="correct-count-cats-error"
                        className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        {t("inventory.catsLoadError", "Could not load categories. Reload to try again — Category changes are disabled until categories are available.")}
                    </div>
                )}
                {error && (
                    <div data-testid="correct-count-error"
                        className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
                        {error}
                    </div>
                )}
                <div className="max-h-[380px] overflow-auto border border-neutral-200 rounded-md">
                    <table className="min-w-full text-sm">
                        <thead className="bg-neutral-50">
                            <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                                <th className="px-3 py-2">{t("inventory.product", "Product")}</th>
                                <th className="px-3 py-2 w-20">{t("inventory.qty", "Qty")}</th>
                                <th className="px-3 py-2 w-16">{t("inventory.unit", "Unit")}</th>
                                <th className="px-3 py-2 w-24">{t("inventory.price", "Price")}</th>
                                <th className="px-3 py-2 w-32">{t("inventory.categoryLocal", "Category (local)")}</th>
                                <th className="px-3 py-2 w-20">VAT %</th>
                                <th className="px-3 py-2 w-16"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it) => {
                                const marked = deletes.has(it.id);
                                const p = patches[it.id] || {};
                                return (
                                    <tr key={it.id}
                                        className={marked ? "line-through text-neutral-400" : ""}
                                        data-testid={`correct-row-${it.id}`}>
                                        <td className="px-3 py-2">{it.product_name}</td>
                                        <td className="px-3 py-2">
                                            <input data-testid={`correct-qty-${it.id}`} type="number"
                                                defaultValue={it.quantity}
                                                disabled={marked}
                                                onChange={(e) => patch(it.id, "quantity", e.target.value)}
                                                className="h-8 w-20 rounded border border-neutral-300 px-1 text-sm" />
                                        </td>
                                        <td className="px-3 py-2">
                                            <input defaultValue={it.unit} disabled={marked}
                                                onChange={(e) => patch(it.id, "unit", e.target.value)}
                                                className="h-8 w-14 rounded border border-neutral-300 px-1 text-sm" />
                                        </td>
                                        <td className="px-3 py-2">
                                            <input type="number" step="0.01"
                                                defaultValue={it.price} disabled={marked}
                                                onChange={(e) => patch(it.id, "price", e.target.value)}
                                                className="h-8 w-24 rounded border border-neutral-300 px-1 text-sm" />
                                        </td>
                                        <td className="px-3 py-2">
                                            {/* Controlled value so a reopened
                                                dialog on a corrected count
                                                still highlights the saved
                                                override, even when the
                                                custom-category list loads
                                                after the items. */}
                                            <select data-testid={`correct-category-${it.id}`}
                                                value={p.category_override !== undefined
                                                        ? p.category_override
                                                        : (it.category_override || "")}
                                                disabled={marked}
                                                onChange={(e) => patch(it.id, "category_override", e.target.value)}
                                                className="h-8 w-full rounded border border-neutral-300 px-1 text-sm">
                                                <option value="">(keep)</option>
                                                {cats.map((cn) => <option key={cn} value={cn}>{cn}</option>)}
                                                {it.category_override
                                                 && !cats.includes(it.category_override)
                                                 && (
                                                    <option key={`__ov_${it.id}`}
                                                        value={it.category_override}>
                                                        {it.category_override}
                                                    </option>
                                                 )}
                                            </select>
                                        </td>
                                        <td className="px-3 py-2">
                                            <input data-testid={`correct-vat-${it.id}`} type="number" step="0.01"
                                                placeholder={String(it.vat ?? "")} disabled={marked}
                                                defaultValue={it.vat_override ?? ""}
                                                onChange={(e) => patch(it.id, "vat_override", e.target.value)}
                                                className="h-8 w-20 rounded border border-neutral-300 px-1 text-sm" />
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <button data-testid={`correct-del-${it.id}`}
                                                onClick={() => toggleDelete(it.id)}
                                                className="text-xs text-red-700 hover:underline">
                                                {marked ? "Undo" : "Remove"}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {adds.map((r) => (
                                <tr key={r._k} className="bg-emerald-50/40"
                                    data-testid={`correct-add-row-${r._k}`}>
                                    <td className="px-3 py-2">
                                        <input placeholder={t("inventory.forgottenProductName", "Forgotten product name")}
                                            value={r.product_name}
                                            onChange={(e) => patchAdd(r._k, "product_name", e.target.value)}
                                            className="h-8 w-full rounded border border-neutral-300 px-1 text-sm" />
                                    </td>
                                    <td className="px-3 py-2">
                                        <input type="number" value={r.quantity}
                                            onChange={(e) => patchAdd(r._k, "quantity", e.target.value)}
                                            className="h-8 w-20 rounded border border-neutral-300 px-1 text-sm" />
                                    </td>
                                    <td className="px-3 py-2">
                                        <input value={r.unit}
                                            onChange={(e) => patchAdd(r._k, "unit", e.target.value)}
                                            className="h-8 w-14 rounded border border-neutral-300 px-1 text-sm" />
                                    </td>
                                    <td className="px-3 py-2">
                                        <input type="number" step="0.01"
                                            disabled={r.missing_price}
                                            value={r.price}
                                            onChange={(e) => patchAdd(r._k, "price", e.target.value)}
                                            className="h-8 w-20 rounded border border-neutral-300 px-1 text-sm" />
                                        <label className="block text-[10px] text-neutral-500 mt-0.5">
                                            <input type="checkbox" checked={r.missing_price}
                                                onChange={(e) => patchAdd(r._k, "missing_price", e.target.checked)} />
                                            {" "}missing
                                        </label>
                                    </td>
                                    <td className="px-3 py-2">
                                        <select value={r.category_override}
                                            onChange={(e) => patchAdd(r._k, "category_override", e.target.value)}
                                            className="h-8 w-full rounded border border-neutral-300 px-1 text-sm">
                                            <option value="">(default)</option>
                                            {cats.map((cn) => <option key={cn} value={cn}>{cn}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-3 py-2">
                                        <input type="number" step="0.01" value={r.vat_override}
                                            onChange={(e) => patchAdd(r._k, "vat_override", e.target.value)}
                                            className="h-8 w-20 rounded border border-neutral-300 px-1 text-sm" />
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <button onClick={() => removeAdd(r._k)}
                                            className="text-xs text-red-700 hover:underline">{t("inventory.discard", "Discard")}</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="flex items-center justify-between">
                    <button data-testid="correct-add-item" onClick={addNew}
                        className="text-xs font-semibold text-emerald-800 underline">
                        + Add forgotten product
                    </button>
                    <button data-testid="correct-show-history" onClick={loadHistory}
                        className="text-xs text-neutral-500 underline">
                        {showHistory ? "Hide" : "Show"} correction history
                    </button>
                </div>
                {showHistory && (
                    <div className="text-xs border border-neutral-200 rounded-md p-2 max-h-40 overflow-auto"
                        data-testid="correct-history">
                        {history.length === 0 ? t("inventory.noCorrectionsYet", "No corrections yet.") :
                            history.map((h) => (
                                <div key={h.id} className="py-1 border-b border-neutral-100 last:border-0">
                                    <b>r{h.revision}</b> · {h.performed_at?.slice(0, 19)?.replace("T", " ")}
                                    {" "}· {h.performed_by_name} · {h.reason}
                                </div>
                            ))}
                    </div>
                )}
                <div className="mt-2">
                    <label className="block text-xs font-semibold text-neutral-700 mb-1">{t("inventory.reasonRequired", "Reason (required)")}</label>
                    <input data-testid="correct-reason" value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={t("inventory.eGSupplierInvoiceReconciliation", "e.g. supplier invoice reconciliation")}
                        className="h-9 w-full rounded-md border border-neutral-300 px-2 text-sm" />
                </div>
                <DialogFooter>
                    <button data-testid="correct-cancel" onClick={onClose}
                        className="h-9 px-3 rounded-md border border-neutral-300 text-sm">{t("inventory.cancel", "Cancel")}</button>
                    <button data-testid="correct-save" onClick={save} disabled={saving}
                        className="h-9 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                        {saving ? t("inventory.saving", "Saving…") : t("inventory.saveCorrection", "Save correction")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function StartCountDialog({ open, onClose, onStarted }) {
    const { t } = useTranslation();
    const { withRestaurant, requireSelection } = useRestaurantScope();
    const [countType, setCountType] = useState("monthly");
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [notes, setNotes] = useState("");
    // Message 405 — canonical Inventory Month (YYYY-MM). Defaults to
    // current UTC month. Independent from count_date; completing next
    // calendar month does not change this.
    const _today = new Date();
    const _defaultMonth = `${_today.getFullYear()}-${String(_today.getMonth() + 1).padStart(2, "0")}`;
    const [inventoryMonth, setInventoryMonth] = useState(_defaultMonth);
    const [types, setTypes] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setCountType("monthly");
            const t = new Date();
            setDate(t.toISOString().slice(0, 10));
            setInventoryMonth(`${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}`);
            setNotes("");
        }
    }, [open]);

    useEffect(() => {
        api.get("/inventory/count-types").then(({ data }) => setTypes(data)).catch(() => setTypes([]));
    }, []);

    const start = async () => {
        // Guard: any non-legacy-restaurant role must have a restaurant
        // selected in the global topbar selector before creating a count.
        // Without it the backend rejects the POST with HTTP 400.
        if (!requireSelection()) return;
        setSaving(true);
        try {
            const body = withRestaurant({
                count_date: date,
                count_type: countType,
                notes,
                inventory_month: inventoryMonth,
            });
            await api.post("/inventory-counts", body);
            toast.success(t("inventory2.countStarted", "Count started"));
            onStarted();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(
                typeof detail === "string"
                    ? detail
                    : t("inventory.couldNotStartTheInventory", "Could not start the inventory count. Please try again."));
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent data-testid="start-count-dialog">
                <DialogHeader>
                    <DialogTitle>{t("inventory.startANewInventoryCount", "Start a new inventory count")}</DialogTitle>
                    <DialogDescription>{t("inventory.chooseTheTypeAndThe", "Choose the type and the count date.")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("inventory.type", "Type")}</label>
                        <Select value={countType} onValueChange={setCountType}>
                            <SelectTrigger data-testid="count-type-select" className="mt-1 h-11"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {types.map((ct) => <SelectItem key={ct.value} value={ct.value}>{t(`inventory.countType.${ct.value}`, ct.label)}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("inventory.inventoryMonth", "Inventory Month")}</label>
                        <input type="month" data-testid="count-inventory-month" value={inventoryMonth}
                            onChange={(e) => setInventoryMonth(e.target.value)}
                            className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md" />
                        <p className="text-[11px] text-neutral-500 mt-1">
                            {t("inventory.canonicalMonthThisCountBelongs", "Canonical month this count belongs to. Completing next month does not change this.")}
                        </p>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("inventory.date", "Date")}</label>
                        <input type="date" data-testid="count-date" value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("inventory.notes", "Notes")}</label>
                        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                            className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md text-sm" />
                    </div>
                </div>
                <DialogFooter>
                    <button data-testid="dialog-cancel-button" onClick={onClose} className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold">{t("inventory.cancel", "Cancel")}</button>
                    <button data-testid="confirm-start-count" onClick={start} disabled={saving}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                        {saving ? t("inventory.starting", "Starting…") : t("inventory.startCount", "Start count")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ActiveCountScreen({ count, onBack, refresh, onComplete }) {
    const { t } = useTranslation();
    const { user, restaurantId } = useApp();
    const { needsExplicit, scopedParams, withRestaurant, requireSelection } = useRestaurantScope();
    const { confirm, ConfirmDialog } = useConfirm();
    const { isFeatureEnabled } = useResolvedConfig();
    const barcodeEnabled = isFeatureEnabled("barcode_scanner");
    const companionEnabled = isFeatureEnabled("phone_companion_capture");
    // All non-restaurant roles (corporate, plus platform-side super_admin
    // acting on a company) must send restaurant_id explicitly — see
    // backend restaurant_scope() at server.py:71.
    const params = scopedParams;

    const [areas, setAreas] = useState([]);
    const [area, setArea] = useState("");
    const [scan, setScan] = useState("");
    const [search, setSearch] = useState("");
    const [results, setResults] = useState([]);
    const [pickedProduct, setPickedProduct] = useState(null);
    const [pickedManual, setPickedManual] = useState(null); // For new product entry
    const [quantity, setQuantity] = useState("");
    const [missingPriceWarn, setMissingPriceWarn] = useState(false);
    const [busy, setBusy] = useState(false);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [phoneOpen, setPhoneOpen] = useState(false);
    // Phase 5 (Message 365) — Edit Product dialog state. Can be
    // opened from the selected-product card OR from a counted row;
    // in both cases we stay on the inventory screen.
    const [editProductFor, setEditProductFor] = useState(null);
    const [editCountedItemId, setEditCountedItemId] = useState(null);
    // Message 460 · Section 4 — Edit Count quantity dialog state.
    // Separate from EditProductDialog (which edits Product Master).
    const [editQtyItem, setEditQtyItem] = useState(null);
    const [suggestion, setSuggestion] = useState(null);  // Prompt 31 — external lookup result
    // Product-level barcode lock. Once the first valid barcode (camera,
    // USB-wedge, Companion or manual) resolves to a picked product /
    // manual entry / suggestion, we freeze that value for the whole
    // entry session. All later barcode inputs are ignored until:
    //   - the current item is saved (cancelPick clears the lock), or
    //   - the user explicitly presses Rescan / Scan Again.
    // Detection, lookup and save paths are untouched — this is a pure
    // guard on the input side.
    const [lockedBarcode, setLockedBarcode] = useState("");
    const lockedRef = useRef(false);
    // Phase 3 — Missing Prices review + post-completion summary.
    const [missingReviewOpen, setMissingReviewOpen] = useState(false);
    // Phase 2 · Uncounted-Review modal — appears when the chef hits
    // Complete but the tenant's active product catalogue contains rows
    // that were never counted in this cycle.
    const [uncountedReview, setUncountedReview] = useState(null);
    const [uncountedSearch, setUncountedSearch] = useState("");
    const [uncountedBusy, setUncountedBusy] = useState(false);
    const [completionSummary, setCompletionSummary] = useState(null);
    const [completionCurrency, setCompletionCurrency] = useState("EUR");
    // Phase 2 — Durable Continuous Scanning. Pending Scans drawer state.
    // Each entry: { id (event_id), barcode, quantity, match, suggestion?, received_at }
    const [pendingEvents, setPendingEvents] = useState([]);
    const [showPending, setShowPending] = useState(false);
    // Current companion session id — used to replay pending events on reconnect.
    const [companionSessionId, setCompanionSessionId] = useState(null);
    const resolvingEventRef = useRef(null);
    const [phoneQtyNote, setPhoneQtyNote] = useState(null);

    const scanRef = useRef(null);

    useEffect(() => {
        api.get("/inventory/areas", { params }).then(({ data }) => setAreas(data));
    }, [params]);
    useEffect(() => {
        scanRef.current?.focus();
    }, []);

    const totalValue = useMemo(() => (count.items || [])
        .reduce((s, i) => s + Number(i.total_value || 0), 0), [count.items]);

    // Resolve a scanned barcode against the full lookup chain:
    //   1) internal DB → pick and open quantity input
    //   2) external providers (GS1 Finland / Open Food Facts)
    //        → show ProductSuggestionDialog for confirm-to-create
    //   3) nothing found → open manual product entry with barcode pre-filled
    // Prices are NEVER imported from external providers — see backend
    // barcode_providers.ProductSuggestion for the enforcement.
    const resolveBarcode = async (code) => {
        const v = (code || "").trim();
        if (!v) return;
        // Product-level lock: if another barcode is already being
        // processed for the current entry, drop this one. Detection
        // itself is not touched — we only ignore the payload here.
        if (lockedRef.current) {
            toast.message(t("inventory.barcodeLocked", { defaultValue: "Barcode locked ({{code}}). Save or Rescan to scan again.", code: lockedBarcode || t("inventory.current", "current") }));
            return;
        }
        // Set the lock optimistically so parallel callbacks (camera +
        // USB wedge + Companion) cannot race past this point.
        lockedRef.current = true;
        setLockedBarcode(v);
        try {
            const { data } = await api.get("/products/barcode-scan", {
                params: { ...params, barcode: v },
            });
            if (data.matched === "internal" && data.product) {
                // Message 460 · Section 2 — barcode scan of an
                // already-counted product must NOT open a new count
                // row. Highlight the existing row instead.
                const dup = (count.items || []).find(
                    (i) => i.product_id === data.product.id);
                if (dup) {
                    toast.warning(t("inventory.alreadyCounted", "Already counted"), {
                        description: t("inventory.alreadyCountedDesc", { defaultValue: "{{name}} · {{qty}} {{unit}} — press Edit count to change.", name: data.product.product_name, qty: dup.quantity, unit: dup.unit || "" }),
                    });
                    highlightCountedRow(dup.id);
                    resolvingEventRef.current = null;
                    // Release the lock so the chef can scan the next item.
                    lockedRef.current = false;
                    setLockedBarcode("");
                    setScan("");
                    return;
                }
                setPickedProduct(data.product);
                setPickedManual(null);
                setQuantity("");
                setScan("");
                return;
            }
            if (data.matched === "external" && data.suggestion) {
                setSuggestion(data.suggestion);
                setScan("");
                return;
            }
            // Not found anywhere → offer manual creation with barcode pre-filled.
            toast.message(t("inventory2.barcodeNotFoundCreateThe", "Barcode not found — create the product manually."));
            setPickedManual({
                product_name: "",
                unit: "kpl",
                category: "General",
                area: area || "Kitchen",
                barcode: v,
            });
            setPickedProduct(null);
            setResults([]);
            setScan("");
        } catch (_e) {
            resolvingEventRef.current = null;
            // Release the lock on lookup failure so the chef can retry.
            lockedRef.current = false;
            setLockedBarcode("");
            toast.error(t("inventory2.lookupFailed", "Lookup failed"));
        }
    };

    const handleScan = async (e) => {
        e.preventDefault();
        await resolveBarcode(scan);
    };

    const onCameraScan = async (code) => {
        setCameraOpen(false);
        await resolveBarcode(code);
        // refocus the visible input so a follow-up USB/BT scan still works
        setTimeout(() => scanRef.current?.focus(), 50);
    };

    const runSearch = async (val) => {
        setSearch(val);
        if (!val.trim()) { setResults([]); return; }
        const { data } = await api.get("/products", {
            params: { ...params, q: val.trim(), limit: 8 },
        });
        setResults(data.items);
    };

    const pickResult = (p) => {
        // Message 460 · Section 2 — Product Master search selection
        // of an already-counted product must highlight the existing
        // row rather than opening the quantity input for a duplicate.
        const dup = (count.items || []).find((i) => i.product_id === p.id);
        if (dup) {
            toast.warning(t("inventory.alreadyCounted", "Already counted"), {
                description: t("inventory.alreadyCountedDesc", { defaultValue: "{{name}} · {{qty}} {{unit}} — press Edit count to change.", name: p.product_name, qty: dup.quantity, unit: dup.unit || "" }),
            });
            highlightCountedRow(dup.id);
            setSearch(""); setResults([]);
            return;
        }
        setPickedProduct(p);
        setPickedManual(null);
        setSearch(""); setResults([]);
        setQuantity("");
    };

    const startManualEntry = () => {
        setPickedManual({
            product_name: search.trim() || "",
            unit: "kpl", category: "General", area: area || "Kitchen", barcode: "",
        });
        setPickedProduct(null);
        setResults([]);
        // Engage the entry-session lock even though there is no
        // scanned barcode yet — this prevents an incoming USB / camera
        // / phone scan from silently replacing the manual entry in
        // progress. cancelPick / saveItem release the lock as usual.
        lockedRef.current = true;
        setLockedBarcode("");
    };

    const cancelPick = () => {
        setPickedProduct(null);
        setPickedManual(null);
        setQuantity("");
        setMissingPriceWarn(false);
        resolvingEventRef.current = null;
        setPhoneQtyNote(null);
        // Release the product-level barcode lock (covers the successful
        // save path — saveItem calls cancelPick() after inserting the
        // count item — and the explicit Rescan / X button paths).
        lockedRef.current = false;
        setLockedBarcode("");
        setTimeout(() => scanRef.current?.focus(), 50);
    };

    // Explicit "Rescan / Scan Again" — clears the current selection
    // safely and re-enables barcode input.
    const rescanBarcode = () => {
        cancelPick();
    };

    const saveItem = async () => {
        const qty = Number(quantity);
        if (!(qty > 0)) { toast.error(t("inventory2.enterAPositiveQuantity", "Enter a positive quantity")); return; }
        setBusy(true);
        try {
            let prod = pickedProduct;
            // If manual entry: create product first
            if (!prod && pickedManual) {
                if (!pickedManual.product_name.trim()) {
                    toast.error(t("inventory2.productNameRequired", "Product name required")); setBusy(false); return;
                }
                const body = withRestaurant({ ...pickedManual });
                const { data } = await api.post("/products", body);
                prod = { id: data.id, ...body, last_price: 0 };
                toast.success(t("inventory2.productCreated", "Product created"));
            }
            const rawUnit = (prod.unit || "").toLowerCase();
            const displayUnit = ["g", "gr", "gram", "grams",
                "gramme", "grammes"].includes(rawUnit)
                ? "kg" : (prod.unit || "kpl");
            const itemBody = {
                product_id: prod.id, product_name: prod.product_name,
                barcode: prod.barcode || "", unit: displayUnit,
                quantity: qty,
                // Phase 5 (Message 365) — legacy Product Master rows
                // can have category/area == null; the backend coerces
                // None → "" defensively but we also strip the null on
                // the wire so old backend versions stay happy.
                category: prod.category || "",
                area: prod.area || "",
                vat: prod.vat || 14,
            };
            const { data } = await api.post(`/inventory-counts/${count.id}/items`, itemBody);
            if (data.missing_price) {
                toast.warning(t("inventory.savedNoPrice", { defaultValue: "Saved — no delivery price available yet for {{name}}", name: prod.product_name }));
            } else {
                // Message 460 — use the effective display/save unit
                // (kg for grams-master products) so the toast matches
                // exactly what was persisted, not the raw master unit.
                toast.success(t("inventory.savedQty", { defaultValue: "Saved: {{qty}} {{unit}} of {{name}}", qty, unit: displayUnit, name: prod.product_name }));
            }
            setMissingPriceWarn(!!data.missing_price);
            if (resolvingEventRef.current) {
                await ackEvent(resolvingEventRef.current, "resolved", { resolved_item_id: data.id });
                resolvingEventRef.current = null;
            }
            await refresh();
            cancelPick();
        } catch (err) {
            // Message 460 · Section 2 — desktop-side 409 already_counted
            // (concurrent race with phone / paste twice) must highlight
            // the existing row instead of raising a generic error.
            const detail = err.response?.data?.detail;
            if (err.response?.status === 409 && detail?.code === "already_counted") {
                toast.warning(translateErrorText(detail.message) || t("inventory.alreadyCounted", "Already counted"), {
                    description: t("inventory.currentEditDesc", { defaultValue: "Current: {{qty}} {{unit}} — press Edit count to change.", qty: detail.existing_quantity, unit: detail.existing_unit || "" }),
                });
                highlightCountedRow(detail.existing_item_id);
                cancelPick();
                return;
            }
            toast.error(translateDetail(detail) || t("inventory.saveFailed2", "Save failed"));
        } finally { setBusy(false); }
    };

    const removeItem = async (itemId) => {
        await api.delete(`/inventory-counts/${count.id}/items/${itemId}`);
        await refresh();
    };

    // Companion capture handler — the phone sends { event_id, barcode,
    // quantity, unit, lookup: {matched, product?, suggestion?} }. If the
    // lookup matched an internal product we auto-save the count item with
    // the phone quantity, then ACK the event so it does not linger in the
    // Pending Scans drawer. For external / unknown matches we pre-fill
    // the desktop inputs AND push the event to the pending list so the
    // operator can resolve them one by one — later scans never overwrite
    // earlier ones (Phase 2 — Durable Continuous Scanning).
    const ackEvent = async (eventId, status = "resolved", extras = {}) => {
        if (!eventId) return;
        try {
            await api.post(`/companion/events/${eventId}/ack`,
                { status, ...extras });
            setPendingEvents((list) => list.filter((e) => e.id !== eventId));
        } catch (_e) { /* leave in queue for retry */ }
    };
    const queuePending = (ev) => setPendingEvents((list) => (
        list.some((e) => e.id === ev.id) ? list : [...list, ev]));
    // Phone scans of known products are saved by the server; the desktop only
    // refreshes. Unresolved scans are queued and never auto-saved here.
    const onCompanionCapture = async (payload) => {
        try {
            if (payload?.kind !== "barcode") return;
            const { event_id, barcode, quantity: qty, lookup, duplicate, item } = payload;
            const phoneQty = Number(qty) > 0 ? Number(qty) : 1;
            if (payload.saved && item) {
                toast.success(t("inventory.phoneSavedQty", { defaultValue: "Phone: {{qty}} {{unit}} of {{name}}", qty: item.quantity, unit: item.unit || "", name: item.product_name }));
                await refresh();
                return;
            }
            if (duplicate?.kind === "already_counted") {
                toast.warning(t("inventory.alreadyCounted", "Already counted"), {
                    description: t("inventory.currentDesktopDesc", { defaultValue: "Current: {{qty}} {{unit}} — edit on desktop to change.", qty: duplicate.current_quantity, unit: duplicate.unit || "" }),
                });
                if (duplicate.existing_item_id) highlightCountedRow(duplicate.existing_item_id);
                return;
            }
            if (!event_id) return;
            queuePending({ id: event_id, barcode, quantity: phoneQty,
                           match: lookup?.matched === "ambiguous" ? "ambiguous" : "none",
                           suggestion: null, received_at: payload.created_at || new Date().toISOString() });
            toast.warning(t("inventory.phoneNoProduct", { defaultValue: "Phone scan {{barcode}} — no product found, added to Pending", barcode }));
        } catch (err) {
            toast.error(translateDetail(err.response?.data?.detail) || t("inventory.phoneCaptureFailed", "Phone capture failed"));
        }
    };

    const startPendingResolve = (ev) => {
        if (lockedRef.current) {
            toast.message(t("inventory2.phoneScanIgnoredCurrentItem", "Phone scan ignored — current item is locked. Save or Rescan first."));
            return false;
        }
        resolvingEventRef.current = ev.id;
        setPhoneQtyNote(ev.quantity ?? null);
        setShowPending(false);
        return true;
    };

    const attachExistingProduct = async (ev, product) => {
        if (!startPendingResolve(ev)) return;
        try {
            const { data: prod } = await api.post(`/companion/events/${ev.id}/attach-barcode`,
                { product_id: product.id });
            const dup = (count.items || []).find((i) => i.product_id === prod.id);
            if (dup) {
                resolvingEventRef.current = null;
                toast.warning(t("inventory.alreadyCounted", "Already counted"), {
                    description: t("inventory.alreadyCountedDesc", { defaultValue: "{{name}} · {{qty}} {{unit}} — press Edit count to change.", name: prod.product_name, qty: dup.quantity, unit: dup.unit || "" }),
                });
                highlightCountedRow(dup.id);
                return;
            }
            setPickedProduct(prod);
            setPickedManual(null);
            setQuantity(String(ev.quantity || ""));
            lockedRef.current = true;
            setLockedBarcode(ev.barcode || "");
        } catch (err) {
            resolvingEventRef.current = null;
            const d = err.response?.data?.detail;
            toast.error(translateErrorText(d?.message) || translateDetail(d) || t("inventory.saveFailed2", "Save failed"));
        }
    };

    const completeCount = async (opts = {}) => {
        if ((count.items || []).length === 0 && !opts.skipEmpty) {
            toast.error(t("inventory2.addAtLeastOneItem", "Add at least one item to complete the count"));
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post(`/inventory-counts/${count.id}/complete`,
                opts.confirmed_missing ? { confirmed_missing: opts.confirmed_missing } : {});
            setCompletionSummary(data.valuation_summary);
            setCompletionCurrency(data.currency || "EUR");
            setUncountedReview(null);
            toast.success(data.confirmed_missing_count
                ? t("inventory.completedWithMissing", { defaultValue: "Inventory completed · {{count}} products confirmed not in stock", count: data.confirmed_missing_count })
                : t("inventory.inventoryCompleted", "Inventory completed"));
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (err.response?.status === 409 &&
                    (detail?.code === "missing_prices" ||
                     detail === "missing_prices" ||
                     (typeof detail === "object" && detail?.missing_count))) {
                setMissingReviewOpen(true);
            } else if (err.response?.status === 409 &&
                         detail?.code === "uncounted_products") {
                // Fetch the uncounted list so the chef can review + confirm.
                try {
                    const { data: review } = await api.get(
                        `/inventory-counts/${count.id}/uncounted-review`);
                    setUncountedReview(review);
                } catch (e) {
                    toast.error(t("inventory2.couldNotLoadUncountedProducts", "Could not load uncounted products"));
                }
            } else {
                toast.error(translateDetail(detail) || t("inventory.couldNotCompleteInventory", "Could not complete inventory"));
            }
        } finally { setBusy(false); }
    };

    const closeSummary = async () => {
        setCompletionSummary(null);
        await onComplete();
    };

    const discardCount = async () => {
        const ok = await confirm({
            title: t("inventory.discardThisInProgressCount", "Discard this in-progress count?"),
            description: t("inventory.allEntriesYouHaveAdded", "All entries you have added will be lost. This cannot be undone."),
            confirmLabel: t("inventory.discardCount", "Discard count"),
            destructive: true,
        });
        if (!ok) return;
        await api.delete(`/inventory-counts/${count.id}`);
        toast.success(t("inventory2.countDiscarded", "Count discarded"));
        await onBack();
    };

    const filteredItems = useMemo(() => (area
        ? (count.items || []).filter((i) => i.area === area)
        : (count.items || [])), [area, count.items]);

    // Message 460 · Section 3 — group the counted items by canonical
    // category, sort A–Z within each category, and emit sequential
    // UI-only numbering (never persisted). Category is display-only:
    // quantity/price/value math is untouched.
    const groupedItems = useMemo(() => {
        const buckets = new Map();
        for (const label of [...CANONICAL_CATEGORY_ORDER, NEEDS_REVIEW_LABEL]) {
            buckets.set(label, []);
        }
        for (const it of filteredItems) {
            // Message 460 · Section 3 — group by CURRENT Product Master
            // category. Backend enriches each row with `current_category`;
            // fall back to the row's snapshot for legacy responses.
            const cat = (it.current_category || it.category || "").trim();
            const bucket = CANONICAL_CATEGORY_ORDER.includes(cat)
                ? cat : NEEDS_REVIEW_LABEL;
            buckets.get(bucket).push(it);
        }
        // Alphabetical sort within each category (case-insensitive).
        for (const list of buckets.values()) {
            list.sort((a, b) => (a.product_name || "").toLowerCase()
                .localeCompare((b.product_name || "").toLowerCase()));
        }
        // Emit non-empty buckets in canonical order.
        let running = 0;
        return [...buckets.entries()]
            .filter(([, list]) => list.length > 0)
            .map(([label, list]) => ({
                label,
                items: list.map((it) => ({ ...it, __seq: ++running })),
            }));
    }, [filteredItems]);

    return (
        <div className="px-6 lg:px-12 py-10 max-w-6xl mx-auto">
            {/* Header */}
            <div data-testid="active-count-header" className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6 mb-8 pb-6 border-b border-neutral-200">
                <div>
                    <button data-testid="back-to-history" onClick={onBack}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-500 hover:text-neutral-900 mb-2">
                        <ArrowLeft className="h-3.5 w-3.5" /> {t("inventory.inventory", "Inventory")}
                    </button>
                    <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
                        {t("inventory.countingNow", "Counting now")}
                    </h1>
                    <p className="mt-2 text-sm text-neutral-500">
                        {formatDateLong(count.count_date)}
                        <span className="capitalize ml-2">· {t(`inventory.countTypeShort.${count.count_type || "monthly"}`, (count.count_type || "monthly").replace("_", " "))}</span>
                        <span className="ml-2">· {t("inventory.itemsCount", { defaultValue: "{{count}} items", count: (count.items || []).length })} · € {totalValue.toFixed(2)}</span>
                    </p>
                </div>
                <div data-testid="active-count-actions" className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
                    <button data-testid="discard-count" onClick={discardCount}
                        className="h-10 px-4 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-red-200 text-red-700 text-sm font-semibold hover:bg-red-50">
                        <Trash2 className="h-4 w-4" /> {t("inventory.discard", "Discard")}
                    </button>
                    <button data-testid="complete-count" onClick={completeCount} disabled={busy}
                        className="h-10 px-4 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60">
                        <Check className="h-4 w-4" /> {t("inventory.completeCount", "Complete Count")}
                    </button>
                </div>
            </div>

            {/* Scan/search row */}
            <section className="kop-card p-5">
                <div className="flex flex-wrap items-center gap-3">
                    <form onSubmit={handleScan} className="flex-1 min-w-[260px]">
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("inventory.scanBarcode", "Scan barcode")}</label>
                        <div data-testid="scan-actions" className={`mt-1 relative flex flex-wrap ${pendingEvents.length > 0 ? "" : "sm:flex-nowrap"} items-center gap-2`}>
                            <div className="relative basis-full sm:basis-auto flex-1">
                                <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                                <input
                                    ref={scanRef}
                                    data-testid="scan-input"
                                    value={scan}
                                    onChange={(e) => setScan(e.target.value)}
                                    placeholder={t("inventory.focusHereAndScan", "Focus here and scan…")}
                                    className="w-full h-12 pl-9 pr-3 rounded-md border border-neutral-300 focus:outline-none focus:border-neutral-900 text-base"
                                />
                            </div>
                            {barcodeEnabled && (
                            <button
                                type="button"
                                data-testid="open-camera-scanner"
                                onClick={() => setCameraOpen(true)}
                                className="h-12 px-3 flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-neutral-300 bg-white text-xs font-semibold text-neutral-700 hover:bg-neutral-50"
                                title={t("inventory.scanWithTheDeviceCamera", "Scan with the device camera")}
                            >
                                <Camera className="h-4 w-4" /> {t("inventory.camera", "Camera")}
                            </button>
                            )}
                            {companionEnabled && (
                            <button
                                type="button"
                                data-testid="use-phone-scanner-btn"
                                onClick={() => {
                                    if (!requireSelection()) return;
                                    setPhoneOpen(true);
                                }}
                                className="h-12 px-3 flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-neutral-300 bg-white text-xs font-semibold text-neutral-700 hover:bg-neutral-50"
                                title={t("inventory.scanBarcodesFromYourPhone", "Scan barcodes from your phone")}
                            >
                                <Smartphone className="h-4 w-4" /> {t("inventory.phone", "Phone")}
                            </button>
                            )}
                            {pendingEvents.length > 0 && (
                                <button
                                    type="button"
                                    data-testid="pending-scans-btn"
                                    onClick={() => setShowPending(true)}
                                    className="h-12 px-3 flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-amber-300 bg-amber-50 text-xs font-semibold text-amber-800 hover:bg-amber-100"
                                    title={t("inventory.unresolvedPhoneScans", "Unresolved phone scans")}
                                >
                                    <ScanLine className="h-4 w-4" />
                                    {t("inventory.pendingScans", "Pending scans")}
                                    <span className="ml-1 rounded-full bg-amber-200 px-1.5 py-0.5 text-[10px] font-bold">
                                        {pendingEvents.length}
                                    </span>
                                </button>
                            )}
                            <button
                                type="button"
                                data-testid="add-manually-btn"
                                onClick={startManualEntry}
                                className="h-12 px-3 flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-neutral-300 bg-white text-xs font-semibold text-neutral-700 hover:bg-neutral-50"
                                title={t("inventory.enterAProductWithoutScanning", "Enter a product without scanning")}
                            >
                                <Plus className="h-4 w-4" /> {t("inventory.addManually", "Add Manually")}
                            </button>
                        </div>
                        {(lockedRef.current || lockedBarcode) && (pickedProduct || pickedManual || suggestion) && (
                            <div
                                data-testid="barcode-lock-pill"
                                className="mt-2 inline-flex items-center gap-2 rounded-full border border-neutral-300 bg-neutral-50 px-3 py-1 text-[11px] font-semibold text-neutral-700"
                            >
                                <ScanLine className="h-3.5 w-3.5" />
                                {lockedBarcode
                                    ? <>{t("inventory.locked", "Locked:")} <span className="font-mono">{lockedBarcode}</span></>
                                    : <>{t("inventory.entryLocked", "Entry locked")}</>}
                                <button
                                    type="button"
                                    data-testid="rescan-barcode-btn"
                                    onClick={rescanBarcode}
                                    className="ml-1 rounded-md bg-neutral-900 px-2 py-0.5 text-[10px] font-bold text-white hover:bg-neutral-800"
                                    title={t("inventory.clearTheCurrentEntryAnd", "Clear the current entry and scan again")}
                                >
                                    {t("inventory.rescan", "Rescan")}
                                </button>
                            </div>
                        )}
                    </form>
                    <div className="flex-1 min-w-[260px]">
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("inventory.orSearchByName", "Or search by name")}</label>
                        <div className="mt-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                            <input
                                data-testid="search-input"
                                value={search}
                                onChange={(e) => runSearch(e.target.value)}
                                placeholder={t("inventory.eGSalmon", "e.g. Salmon")}
                                className="w-full h-12 pl-9 pr-3 rounded-md border border-neutral-300 focus:outline-none focus:border-neutral-900 text-base"
                            />
                        </div>
                    </div>
                    <div className="w-48">
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("inventory.areaFilter", "Area filter")}</label>
                        <Select value={area || "_all"} onValueChange={(v) => setArea(v === "_all" ? "" : v)}>
                            <SelectTrigger data-testid="area-filter" className="mt-1 h-12"><SelectValue placeholder={t("inventory.allAreas", "All areas")} /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="_all">{t("inventory.allAreas", "All areas")}</SelectItem>
                                {areas.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Search results */}
                {results.length > 0 && (
                    <ul className="mt-3 border border-neutral-200 rounded-md divide-y divide-neutral-100 max-h-72 overflow-y-auto"
                        data-testid="search-results">
                        {results.map((p) => (
                            <li key={p.id}>
                                <button onClick={() => pickResult(p)}
                                    data-testid={`pick-product-${p.id}`}
                                    className="w-full text-left px-4 py-2 hover:bg-neutral-50 flex items-center justify-between">
                                    <div>
                                        <div className="font-medium text-sm">{p.product_name}</div>
                                        <div className="text-xs text-neutral-500">
                                            {catLabel(p.category)} · {p.area} · {t("inventory.lastShort", "last")} € {Number(p.last_price || 0).toFixed(2)} / {p.unit}
                                            {p.barcode && <span> · {p.barcode}</span>}
                                        </div>
                                    </div>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                {search.trim() && results.length === 0 && (
                    <div className="mt-3 text-sm text-neutral-500">
                        {t("inventory.noMatchingProducts", "No matching products.")}
                        <button data-testid="create-new-from-search" onClick={startManualEntry}
                            className="ml-2 font-semibold text-neutral-900 underline">{t("inventory.createNamed", { defaultValue: "Create \"{{name}}\"", name: search.trim() })}</button>
                    </div>
                )}
            </section>

            {/* Quantity entry */}
            {(pickedProduct || pickedManual) && (
                <section className="mt-4 kop-card p-5" data-testid="quantity-panel">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="font-display text-xl font-bold">
                                {pickedProduct?.product_name || pickedManual?.product_name}
                            </div>
                            <div className="text-xs text-neutral-500 mt-1">
                                {(pickedProduct?.category || pickedManual?.category)} · {(pickedProduct?.area || pickedManual?.area)} ·
                                last € {Number(pickedProduct?.last_price || 0).toFixed(2)} / {pickedProduct?.unit || pickedManual?.unit}
                            </div>
                        </div>
                        <button onClick={cancelPick} className="h-9 w-9 grid place-items-center rounded-md hover:bg-neutral-100">
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    {pickedManual && (
                        <div className="mt-4 grid grid-cols-2 gap-2">
                            <input data-testid="manual-name" placeholder={t("inventory.productName", "Product name *")}
                                value={pickedManual.product_name}
                                onChange={(e) => setPickedManual({ ...pickedManual, product_name: e.target.value })}
                                className="col-span-2 h-10 px-3 border border-neutral-300 rounded text-sm" />
                            <input data-testid="manual-unit" placeholder={t("inventory.unitKgLKpl", "Unit (kg/L/kpl)")}
                                value={pickedManual.unit}
                                onChange={(e) => setPickedManual({ ...pickedManual, unit: e.target.value })}
                                className="h-10 px-3 border border-neutral-300 rounded text-sm" />
                            <input data-testid="manual-area" placeholder="Area"
                                value={pickedManual.area}
                                onChange={(e) => setPickedManual({ ...pickedManual, area: e.target.value })}
                                className="h-10 px-3 border border-neutral-300 rounded text-sm" />
                            <input data-testid="manual-category" placeholder={t("inventory.category", "Category")}
                                value={pickedManual.category}
                                onChange={(e) => setPickedManual({ ...pickedManual, category: e.target.value })}
                                className="h-10 px-3 border border-neutral-300 rounded text-sm" />
                            <input data-testid="manual-barcode" placeholder={t("inventory.barcodeOptional", "Barcode (optional)")}
                                value={pickedManual.barcode}
                                onChange={(e) => setPickedManual({ ...pickedManual, barcode: e.target.value })}
                                className="h-10 px-3 border border-neutral-300 rounded text-sm" />
                        </div>
                    )}

                    <div className="mt-4 flex flex-wrap items-end gap-3">
                        <div className="flex-1 min-w-[200px]">
                            {/* Phase 10 · Inventory Count unit visible
                                next to the quantity field so the chef
                                never has to guess whether they are
                                entering kg / L / kpl. See RCA 2026-02.
                                Message 460 — for weight products the
                                displayed / entered unit is standardized
                                to KG regardless of the Product Master
                                base unit (grams). The backend accepts
                                either and normalizes to KG on write. */}
                            {(() => {
                                const rawUnit = ((pickedProduct || pickedManual)?.unit || "").toLowerCase();
                                const displayUnit = ["g", "gr", "gram", "grams",
                                    "gramme", "grammes"].includes(rawUnit) ? "kg" : ((pickedProduct || pickedManual)?.unit || "unit");
                                return (
                                    <>
                                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">
                                            Quantity <span className="text-neutral-400">(in <b data-testid="quantity-unit-label">{displayUnit}</b>)</span>
                                        </label>
                                        <div className="mt-1 relative">
                                            <input
                                                data-testid="quantity-input"
                                                type="number" min="0" step="0.01" autoFocus
                                                value={quantity}
                                                onChange={(e) => setQuantity(e.target.value)}
                                                onKeyDown={(e) => e.key === "Enter" && saveItem()}
                                                placeholder="0"
                                                className="w-full h-14 pl-4 pr-16 border border-neutral-300 rounded-md focus:outline-none focus:border-neutral-900 text-2xl font-display font-bold"
                                            />
                                            <span data-testid="quantity-unit-suffix"
                                                  className="absolute right-4 top-1/2 -translate-y-1/2 text-lg font-semibold text-neutral-500 select-none">
                                                {displayUnit}
                                            </span>
                                        </div>
                                        {phoneQtyNote != null && (
                                            <div data-testid="phone-qty-confirm-note"
                                                 className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                                {t("inventory.phoneQtyConfirm", { defaultValue: "Phone entered {{qty}}. Confirm it is in {{unit}} before saving.", qty: phoneQtyNote, unit: displayUnit })}
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                        <button data-testid="save-item-button" onClick={saveItem} disabled={busy}
                            className="h-14 px-6 rounded-md bg-neutral-900 text-white font-semibold text-base hover:bg-neutral-800 disabled:opacity-60">
                            Save & next
                        </button>
                    </div>
                    {pickedProduct && (
                        <div className="mt-2 text-right">
                            <button data-testid="edit-selected-product"
                                onClick={() => setEditProductFor(pickedProduct)}
                                className="text-xs font-semibold text-neutral-600 hover:text-neutral-900 underline underline-offset-2">
                                {t("inventory.editProduct", "Edit product")}
                            </button>
                            {!pickedProduct.category && (
                                <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                                    {t("inventory.needsReview", "Needs review")}
                                </span>
                            )}
                        </div>
                    )}
                    {missingPriceWarn && (
                        <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-md"
                            data-testid="missing-price-warn">
                            {t("inventory.noDeliveryPriceAvailableQuantity", "No delivery price available — quantity saved, value will appear after the next delivery.")}
                        </div>
                    )}
                </section>
            )}

            {/* Counted items list */}
            <section className="mt-8">
                <h2 className="font-display text-xl font-bold mb-3">
                    {t("inventory.countedSoFar", "Counted so far")} {area && <span className="text-base font-normal text-neutral-500">· {area}</span>}
                </h2>
                {filteredItems.length === 0 ? (
                    <div className="kop-card p-6 text-sm text-neutral-500">
                        {t("inventory.nothingCountedYetScanA", "Nothing counted yet. Scan a barcode or search a product to start.")}
                    </div>
                ) : (
                    <div className="kop-card overflow-x-auto" data-testid="counted-items-table">
                        <table className="w-full text-sm">
                            <thead className="bg-neutral-50">
                                <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                                    <th className="px-4 py-2 font-semibold w-10">#</th>
                                    <th className="px-4 py-2 font-semibold">{t("inventory.product", "Product")}</th>
                                    <th className="px-4 py-2 font-semibold">{t("inventory.area", "Area")}</th>
                                    <th className="px-4 py-2 font-semibold">{t("inventory.qty", "Qty")}</th>
                                    <th className="px-4 py-2 font-semibold">{t("inventory.unit", "Unit")}</th>
                                    <th className="px-4 py-2 font-semibold">{t("inventory.price", "Price")}</th>
                                    <th className="px-4 py-2 font-semibold">{t("inventory.value", "Value")}</th>
                                    <th className="px-4 py-2"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-100">
                                {groupedItems.map((group) => (
                                    <React.Fragment key={group.label}>
                                        <tr data-testid={`category-header-${group.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                            className="bg-neutral-100/70">
                                            <td colSpan={8} className="px-4 py-2 text-[11px] uppercase tracking-wide font-bold text-neutral-700">
                                                {catLabel(group.label)}
                                                <span className="ml-2 text-[10px] font-semibold text-neutral-500">
                                                    · {group.items.length}
                                                </span>
                                            </td>
                                        </tr>
                                        {group.items.map((it) => (
                                            <tr key={it.id} data-item-id={it.id}
                                                data-testid={`counted-row-${it.id}`}
                                                className="transition-colors">
                                                <td data-testid={`counted-seq-${it.id}`}
                                                    className="px-4 py-2 text-xs font-mono text-neutral-500 tabular-nums">
                                                    {it.__seq}.
                                                </td>
                                                <td className="px-4 py-2 font-medium">
                                                    {it.product_name}
                                                    {it.missing_price && (
                                                        <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-700">
                                                            no price
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2 text-neutral-600">{it.area || "—"}</td>
                                                <td className="px-4 py-2">{it.quantity}</td>
                                                <td className="px-4 py-2 text-neutral-600">{it.unit}</td>
                                                <td className="px-4 py-2 text-neutral-600">
                                                    {it.missing_price ? "—" : `€ ${Number(it.price).toFixed(2)}`}
                                                </td>
                                                <td className="px-4 py-2">
                                                    {it.missing_price ? (
                                                        <span data-testid={`no-price-${it.id}`}
                                                                className="text-amber-700 font-semibold">—</span>
                                                    ) : `€ ${Number(it.total_value || 0).toFixed(2)}`}
                                                </td>
                                                <td className="px-4 py-2 text-right whitespace-nowrap">
                                                    <button data-testid={`edit-count-qty-${it.id}`}
                                                        onClick={() => setEditQtyItem(it)}
                                                        className="h-8 px-2 rounded-md text-xs font-semibold text-neutral-700 hover:text-neutral-900 hover:bg-neutral-100">
                                                        {t("inventory.editCount", "Edit count")}
                                                    </button>
                                                    <button data-testid={`edit-counted-${it.id}`}
                                                        onClick={async () => {
                                                            if (!it.product_id) return;
                                                            try {
                                                                const { data } = await api.get(
                                                                    `/products/${it.product_id}`,
                                                                    { params }
                                                                );
                                                                setEditProductFor(data);
                                                                setEditCountedItemId(it.id);
                                                            } catch (e) {
                                                                toast.error(t("inventory2.couldNotLoadProduct", "Could not load product"));
                                                            }
                                                        }}
                                                        className="ml-1 h-8 px-2 rounded-md text-xs font-semibold text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100">
                                                        {t("inventory.product", "Product")}
                                                    </button>
                                                    <button data-testid={`remove-item-${it.id}`} onClick={() => removeItem(it.id)}
                                                        className="ml-1 h-8 w-8 grid place-items-center rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50">
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
            <EditCountQuantityDialog
                item={editQtyItem}
                countId={count?.id}
                onClose={() => setEditQtyItem(null)}
                onSaved={async () => {
                    setEditQtyItem(null);
                    if (refresh) await refresh();
                }}
            />
            <ConfirmDialog />
            <EditProductDialog
                open={!!editProductFor}
                product={editProductFor}
                countedItemId={editCountedItemId}
                countId={count?.id}
                params={params}
                onClose={() => { setEditProductFor(null); setEditCountedItemId(null); }}
                onSaved={async (updatedProduct) => {
                    // Refresh selected-product card if it's the same
                    // product currently in the picker.
                    if (pickedProduct?.id === updatedProduct.id) {
                        setPickedProduct(updatedProduct);
                    }
                    // Refresh the count so the counted row displays
                    // the newly-resolved price.
                    if (refresh) await refresh();
                    setEditProductFor(null);
                    setEditCountedItemId(null);
                }}
            />
            <MissingPricesReview
                countId={count?.id}
                open={missingReviewOpen}
                onClose={async () => {
                    setMissingReviewOpen(false);
                    // Refresh the parent count so the drawer values line up.
                    if (refresh) await refresh();
                }}
                onResolved={async () => {
                    if (refresh) await refresh();
                }}
            />
            <UncountedReviewModal
                review={uncountedReview}
                search={uncountedSearch}
                setSearch={setUncountedSearch}
                busy={uncountedBusy || busy}
                onGoBack={() => { setUncountedReview(null); setUncountedSearch(""); }}
                onConfirm={async () => {
                    setUncountedBusy(true);
                    try {
                        await completeCount({
                            confirmed_missing: (uncountedReview?.uncounted || [])
                                .map((r) => r.product_id),
                        });
                    } finally { setUncountedBusy(false); }
                }}
            />
            <InventoryCompletionSummary
                open={!!completionSummary}
                summary={completionSummary}
                currency={completionCurrency}
                onClose={closeSummary}
            />
            <BarcodeScanner
                open={cameraOpen}
                onClose={() => setCameraOpen(false)}
                onScan={onCameraScan}
            />
            <PhoneCompanionModal
                open={phoneOpen}
                mode="barcode"
                restaurantId={needsExplicit ? restaurantId : undefined}
                inventoryCountId={count?.id}
                onCapture={onCompanionCapture}
                onSessionChange={async (sid) => {
                    setCompanionSessionId(sid);
                    if (!sid) return;
                    // Phase 2 — on (re)connect, pull every pending event
                    // from the server so events that fired while the SSE
                    // stream was reconnecting are not lost.
                    try {
                        const { data } = await api.get(
                            `/companion/session/${sid}/events`,
                            { params: { status: "pending" } });
                        if (Array.isArray(data) && data.length) {
                            // Replay through onCompanionCapture so the
                            // same auto-resolve / pending-drawer logic
                            // fires for late-arriving events.
                            for (const ev of data) {
                                await onCompanionCapture({
                                    kind: ev.kind, event_id: ev.id,
                                    barcode: ev.barcode, quantity: ev.quantity,
                                    unit: ev.unit, lookup: ev.lookup,
                                    created_at: ev.created_at,
                                });
                            }
                        }
                    } catch (_e) { /* best-effort */ }
                }}
                onClose={() => { setPhoneOpen(false); setCompanionSessionId(null); }}
            />
            {suggestion && (
                <ProductSuggestionDialog
                    suggestion={suggestion}
                    restaurantId={params.restaurant_id}
                    onClose={() => { setSuggestion(null); resolvingEventRef.current = null; setPhoneQtyNote(null); }}
                    onCreated={(created) => {
                        // Phase 2 — if this suggestion came from a pending
                        // Companion event, resolve+ack it now that the
                        // product is created.
                        const pendingId = suggestion?.__pending_event_id;
                        setSuggestion(null);
                        api.get("/products/lookup", { params: { ...params, barcode: created.barcode } })
                            .then(({ data }) => {
                                if (data) {
                                    setPickedProduct(data);
                                    setPickedManual(null);
                                    setQuantity(String(quantity || "1"));
                                }
                                setTimeout(() => scanRef.current?.focus(), 50);
                            });
                        // Pending scan is acked only after the count row is saved (saveItem).
                        if (pendingId) resolvingEventRef.current = pendingId;
                    }}
                />
            )}
            <PendingScansDrawer
                open={showPending}
                events={pendingEvents}
                onClose={() => setShowPending(false)}
                onResolveSuggestion={(ev) => {
                    // Feed the suggestion into ProductSuggestionDialog with
                    // the phone quantity pre-filled; ack the event only
                    // after the product creation succeeds (in onCreated).
                    setScan(ev.barcode || "");
                    setQuantity(String(ev.quantity || 1));
                    setSuggestion({ ...(ev.suggestion || {}),
                                     __pending_event_id: ev.id });
                    setShowPending(false);
                }}
                onResolveManual={async (ev) => {
                    // Existing desktop lookup/create flow; acked after the count row is saved.
                    if (!startPendingResolve(ev)) return;
                    await resolveBarcode(ev.barcode || "");
                    setQuantity(String(ev.quantity || ""));
                }}
                onUseExisting={attachExistingProduct}
                searchProducts={async (q) => {
                    const { data } = await api.get("/products", { params: { ...params, q, limit: 8 } });
                    return data.items || [];
                }}
                onDismiss={async (ev) => {
                    if (ev.id) {
                        try { await api.post(`/companion/events/${ev.id}/ack`,
                            { status: "dismissed" }); } catch (_e) { /* best-effort */ }
                    }
                    setPendingEvents((list) => list.filter((e) => e.id !== ev.id));
                }}
            />
        </div>
    );
}

function HistoryDetailDialog({ open, onClose, count }) {
    const { t } = useTranslation();
    if (!count) return null;
    const comp = count.comparison;
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-3xl" data-testid="count-detail-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl font-bold">
                        Inventory · {formatDateLong(count.count_date)}
                    </DialogTitle>
                    <DialogDescription>
                        {t(`inventory.countTypeShort.${count.count_type || "monthly"}`, (count.count_type || "monthly").replace("_", " "))} · {t("inventory.completedBy2", "completed by")} {count.completed_by || "—"}
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-3 gap-4">
                    <div className="kop-card p-4">
                        <div className="text-xs uppercase tracking-wide text-neutral-500">{t("inventory.productsCounted", "Products counted")}</div>
                        {/* Option B · Message 465 — matches the Inventory
                            XLSX/PDF product population exactly: positive
                            on-hand quantity AND not confirmed_missing.
                            Zero-quantity and confirmed_missing rows remain
                            stored on the count but are not displayed here. */}
                        <div className="mt-1 font-display text-3xl font-extrabold" data-testid="count-detail-products-counted">
                            {(count.items || []).filter((it) => (Number(it.quantity) || 0) > 0 && (it.count_status || "counted") !== "confirmed_missing").length}
                        </div>
                    </div>
                    <div className="kop-card p-4" data-testid="inv-modal-totals">
                        {/* Message 405 — VAT 0 / VAT / TOTAL. Values come from
                            the authoritative accounting dataset so this
                            reconciles to the cent with Reports/XLSX/PDF.
                            Legacy `count.total_value` scalar deprecated here. */}
                        <div className="text-xs uppercase tracking-wide text-neutral-500">{t("inventory.inventoryValue", "Inventory value")}</div>
                        {count._accounting ? (
                            <div className="mt-1 space-y-0.5">
                                <div className="flex justify-between text-sm">
                                    <span className="text-neutral-500">VAT 0</span>
                                    <span data-testid="inv-modal-net">€ {Number(count._accounting.total_net || 0).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-neutral-500">VAT</span>
                                    <span data-testid="inv-modal-vat">€ {Number(count._accounting.total_vat || 0).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-base font-bold pt-1 border-t border-neutral-100">
                                    <span>{t("inventory.total", "Total")}</span>
                                    <span data-testid="inv-modal-gross">€ {Number(count._accounting.total_incl_vat || 0).toFixed(2)}</span>
                                </div>
                            </div>
                        ) : (
                            <div className="mt-1 font-display text-3xl font-extrabold">€ {Number(count.total_value || 0).toFixed(2)}</div>
                        )}
                    </div>
                    <div className="kop-card p-4">
                        <div className="text-xs uppercase tracking-wide text-neutral-500">{t("inventory.vsPrevious", "vs previous")}</div>
                        {comp ? (
                            <div className="mt-1 font-display text-3xl font-extrabold">
                                <span className={comp.value_diff >= 0 ? "text-emerald-700" : "text-red-700"}>
                                    {comp.value_diff >= 0 ? "+" : ""}€ {comp.value_diff.toFixed(2)}
                                </span>
                                <div className="text-xs text-neutral-500 mt-1">{t("inventory.since", { defaultValue: "since {{date}}", date: comp.previous_date })}</div>
                            </div>
                        ) : (
                            <div className="mt-1 text-sm text-neutral-500">{t("inventory.noPreviousCount", "No previous count.")}</div>
                        )}
                    </div>
                </div>

                <section className="mt-5">
                    <h4 className="font-semibold text-sm mb-2">{t("inventory.byCategory", "By category")}</h4>
                    {(count.by_category || []).length === 0 ? (
                        <div className="text-sm text-neutral-500 italic">{t("inventory.noData", "No data.")}</div>
                    ) : (
                        <ul className="text-sm divide-y divide-neutral-100 border border-neutral-200 rounded-md overflow-hidden"
                            data-testid="by-category">
                            {count.by_category.map((c) => (
                                <li key={c.category} className="px-4 py-2 flex items-center justify-between">
                                    <span className="font-medium">{catLabel(c.category)}</span>
                                    <span className="text-neutral-600">€ {c.total_value.toFixed(2)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section className="mt-5">
                    <h4 className="font-semibold text-sm mb-2">{t("inventory.byArea", "By area")}</h4>
                    {(count.by_area || []).length === 0 ? (
                        <div className="text-sm text-neutral-500 italic">{t("inventory.noData", "No data.")}</div>
                    ) : (
                        <ul className="text-sm divide-y divide-neutral-100 border border-neutral-200 rounded-md overflow-hidden"
                            data-testid="by-area">
                            {count.by_area.map((c) => (
                                <li key={c.area} className="px-4 py-2 flex items-center justify-between">
                                    <span className="font-medium">{c.area}</span>
                                    <span className="text-neutral-600">€ {c.total_value.toFixed(2)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                {comp && (comp.new_products.length > 0 || comp.removed_products.length > 0) && (
                    <section className="mt-5 grid grid-cols-2 gap-4">
                        <div>
                            <h4 className="font-semibold text-sm mb-2">{t("inventory.newProducts", "New products")}</h4>
                            <ul className="text-sm text-neutral-600 list-disc pl-5">
                                {comp.new_products.slice(0, 10).map((n) => <li key={n}>{n}</li>)}
                                {comp.new_products.length === 0 && <li className="list-none italic text-neutral-400">{t("inventory.none", "None")}</li>}
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold text-sm mb-2">{t("inventory.removedProducts", "Removed products")}</h4>
                            <ul className="text-sm text-neutral-600 list-disc pl-5">
                                {comp.removed_products.slice(0, 10).map((n) => <li key={n}>{n}</li>)}
                                {comp.removed_products.length === 0 && <li className="list-none italic text-neutral-400">{t("inventory.none", "None")}</li>}
                            </ul>
                        </div>
                    </section>
                )}
            </DialogContent>
        </Dialog>
    );
}

function ProductsDialog({ open, onClose }) {
    const { t } = useTranslation();
    const { scopedParams: params } = useRestaurantScope();
    const { user } = useApp();
    const [products, setProducts] = useState({ items: [], total: 0 });
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("available");
    const [addOpen, setAddOpen] = useState(false);
    // 2026-02 · Product Review category dropdown loads the merged
    // built-in + custom category list so restaurant users can pick
    // company-defined categories (e.g. "Cafés") and add new ones
    // inline via the "+ Add category" row at the bottom of the same
    // dropdown. No separate page, no second selector.
    const [categoryList, setCategoryList] = useState([]);

    // Phase 1 · Categorization — manager/admin roles can edit the
    // canonical category. `read_only` and unauthenticated states can
    // still view it. Backend enforces the truth via `require_writer`
    // and `restaurant_scope`.
    const _role = user?.profile_role || user?.role;
    const canEditCategory = !!user
        && _role !== "read_only"
        && ["corporate", "super_admin", "restaurant",
             "restaurant_admin", "head_chef"].includes(_role);

    const load = async () => {
        const { data } = await api.get("/products", {
            params: { ...params, q, status, limit: 200 },
        });
        setProducts(data);
    };

    const loadCategories = async () => {
        try {
            const { data } = await api.get("/categories", {
                params: { type: "product", include_builtin: 1 },
            });
            setCategoryList(Array.isArray(data) ? data : []);
        } catch (_e) { /* Non-fatal: dropdown falls back to empty. */ }
    };

    const loadRef = useLatest(load);
    useEffect(() => { if (open) { loadRef.current(); loadCategories(); } }, [open, status, loadRef]);

    const archive = async (id) => {
        await api.post(`/products/${id}/archive`);
        toast.success(t("inventory2.productArchived", "Product archived"));
        load();
    };
    const activate = async (id) => {
        await api.post(`/products/${id}/activate`);
        toast.success(t("inventory2.productReactivated", "Product reactivated"));
        load();
    };

    return (
        <>
            <Dialog open={open} onOpenChange={onClose}>
                <DialogContent className="max-w-3xl" data-testid="products-dialog">
                    <DialogHeader>
                        <DialogTitle>{t("inventory.products", "Products")}</DialogTitle>
                        <DialogDescription>{t("inventory.restaurantSpecificProductCatalogue", "Restaurant-specific product catalogue.")}</DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                            <input data-testid="product-search" value={q}
                                onChange={(e) => setQ(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && load()}
                                placeholder={t("inventory.searchByNameOrBarcode", "Search by name or barcode")}
                                className="w-full h-10 pl-9 pr-3 rounded-md border border-neutral-200 focus:outline-none focus:border-neutral-900" />
                        </div>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger data-testid="product-status-filter" className="h-10 w-44"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="available">{t("inventory.available", "Available")}</SelectItem>
                                <SelectItem value="archived">{t("inventory.archived", "Archived")}</SelectItem>
                                <SelectItem value="all">{t("inventory.all", "All")}</SelectItem>
                            </SelectContent>
                        </Select>
                        <button data-testid="add-product-button" onClick={() => setAddOpen(true)}
                            className="h-10 px-3 inline-flex items-center gap-2 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800">
                            <Plus className="h-4 w-4" /> {t("inventory.new", "New")}
                        </button>
                    </div>
                    <div className="kop-card overflow-x-auto max-h-[55vh] overflow-y-auto">
                        <table className="w-full text-sm" data-testid="products-table">
                            <thead className="bg-neutral-50 sticky top-0">
                                <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                                    <th className="px-4 py-2 font-semibold">{t("inventory.product", "Product")}</th>
                                    <th className="px-4 py-2 font-semibold">{t("inventory.category", "Category")}</th>
                                    <th className="px-4 py-2 font-semibold">{t("inventory.area", "Area")}</th>
                                    <th className="px-4 py-2 font-semibold">{t("inventory.lastPrice", "Last price")}</th>
                                    <th className="px-4 py-2 font-semibold">{t("inventory.unit", "Unit")}</th>
                                    <th className="px-4 py-2 font-semibold">{t("inventory.status", "Status")}</th>
                                    <th className="px-4 py-2"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-100">
                                {products.items.map((p) => (
                                    <tr key={p.id} data-testid={`product-row-${p.id}`}>
                                        <td className="px-4 py-2 font-medium">{p.product_name}
                                            {p.barcode && <span className="ml-2 text-xs text-neutral-400">{p.barcode}</span>}
                                        </td>
                                        <td className="px-4 py-2">
                                            <ProductCategoryCell
                                                product={p}
                                                canEdit={canEditCategory}
                                                categories={categoryList}
                                                onCategoriesChanged={loadCategories}
                                                onSaved={(updated) => setProducts((s) => ({
                                                    ...s,
                                                    items: s.items.map((x) => x.id === updated.id ? updated : x),
                                                }))}
                                            />
                                        </td>
                                        <td className="px-4 py-2 text-neutral-600">{p.area}</td>
                                        <td className="px-4 py-2">€ {Number(p.last_price || 0).toFixed(2)}</td>
                                        <td className="px-4 py-2 text-neutral-600">{p.unit}</td>
                                        <td className="px-4 py-2">
                                            {p.archived ? <StatusBadge status="action_required">{t("inventory.archived2", "archived")}</StatusBadge>
                                                : p.active ? <StatusBadge status="completed">{t("inventory.available2", "available")}</StatusBadge>
                                                  : <StatusBadge status="pending">{t("inventory.inactive", "inactive")}</StatusBadge>}
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                            {p.archived ? (
                                                <button data-testid={`activate-product-${p.id}`} onClick={() => activate(p.id)}
                                                    className="h-8 px-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 rounded-md">{t("inventory.activate", "Activate")}</button>
                                            ) : (
                                                <button data-testid={`archive-product-${p.id}`} onClick={() => archive(p.id)}
                                                    className="h-8 px-2 text-xs font-semibold text-neutral-600 hover:bg-neutral-50 rounded-md">{t("inventory.archive", "Archive")}</button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {products.items.length === 0 && (
                                    <tr><td colSpan={7} className="px-4 py-4 text-sm text-neutral-500 italic">{t("inventory.noProducts", "No products.")}</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="text-xs text-neutral-500">{products.total} total</div>
                </DialogContent>
            </Dialog>
            <AddProductDialog open={addOpen} onClose={() => setAddOpen(false)}
                onSaved={() => { setAddOpen(false); load(); }} />
        </>
    );
}

// ─────────────────────────────────────────────────────────────
// Phase 2 · Uncounted-Review modal (2026-02-28)
//   Appears when the chef hits Complete but the tenant's active
//   product catalogue still has canonical rows without a count
//   line. The chef either goes back to keep counting OR confirms
//   the products are not currently in stock — which writes zero
//   rows with `count_status = "confirmed_missing"` server-side.
// ─────────────────────────────────────────────────────────────
function UncountedReviewModal({ review, search, setSearch, busy, onGoBack, onConfirm }) {
    const { t } = useTranslation();
    if (!review) return null;
    const list = (review.uncounted || []).filter((r) => {
        const q = (search || "").toLowerCase().trim();
        if (!q) return true;
        return (r.product_name || "").toLowerCase().includes(q)
            || (r.category || "").toLowerCase().includes(q);
    });
    return (
        <Dialog open={!!review} onOpenChange={(o) => { if (!o) onGoBack(); }}>
            <DialogContent className="max-w-2xl" data-testid="uncounted-review-modal">
                <DialogHeader>
                    <DialogTitle>{t("inventory.someProductsWereNotCounted", "Some products were not counted")}</DialogTitle>
                </DialogHeader>
                <div className="text-sm text-neutral-700 mb-3">
                    <span className="font-semibold text-red-700"
                          data-testid="uncounted-total">{review.uncounted_total}</span>
                    {" "}products in this restaurant have no count line. Please confirm they are
                    not currently in stock, or return to the inventory to continue counting.
                </div>
                <Input
                    data-testid="uncounted-search"
                    placeholder={t("inventory.searchUncountedProducts", "Search uncounted products…")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="mb-3"
                />
                <div className="max-h-72 overflow-auto border rounded-md divide-y divide-neutral-100">
                    {list.slice(0, 400).map((r) => (
                        <div key={r.product_id}
                             className="px-3 py-2 flex items-center justify-between gap-3 text-sm">
                            <div className="min-w-0 truncate">
                                <span className="font-medium">{r.product_name}</span>
                                <span className="ml-2 text-neutral-400 text-xs">{r.unit}</span>
                            </div>
                            {r.category
                                ? <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-700 shrink-0">{catLabel(r.category)}</span>
                                : <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 shrink-0">{t("inventory.needsReview2", "Needs Review")}</span>}
                        </div>
                    ))}
                    {list.length === 0 && (
                        <div className="px-3 py-4 text-xs text-neutral-500 italic">{t("inventory.noMatches", "No matches.")}</div>
                    )}
                    {list.length > 400 && (
                        <div className="px-3 py-2 text-xs text-neutral-500 italic">
                            {t("inventory.showingFirst400", { defaultValue: "Showing first 400 of {{n}}. Confirm applies to ALL uncounted products.", n: list.length })}
                        </div>
                    )}
                </div>
                <DialogFooter className="mt-4 gap-2">
                    <Button variant="outline" onClick={onGoBack} disabled={busy}
                            data-testid="uncounted-go-back">
                        {t("inventory.goBackToInventory", "Go back to inventory")}
                    </Button>
                    <Button onClick={onConfirm} disabled={busy}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid="uncounted-confirm">
                        Confirm — not in stock
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// ─────────────────────────────────────────────────────────────
// Phase 1 · Smart Categorization (2026-02-28)
//   ProductCategoryCell renders a category badge + inline dropdown.
//   • Six official categories only — no free text, no seventh option.
//   • `null` category renders as a red "Needs Review" badge so the
//     chef can spot it on the products table.
//   • Manual pick calls PATCH /api/products/{id} which stamps
//     `category_source = "manual"` → wins forever.
//   • `canEdit=false` → read-only badge (no dropdown).
// ─────────────────────────────────────────────────────────────
const CATEGORY_OPTIONS = [
    "Vegetables & Fruit",
    "Meat & Poultry",
    "Dairy",
    "Bakery & Pastry",
    "Dry",
    "Sodas",
    "Alcoholic Beverages",
];

function ProductCategoryCell({ product, canEdit, categories, onCategoriesChanged, onSaved }) {
    const { t } = useTranslation();
    const { scopedParams: params } = useRestaurantScope();
    const [saving, setSaving] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [newName, setNewName] = useState("");
    const [creating, setCreating] = useState(false);
    const cat = product.category;
    const src = product.category_source;

    // 2026-02 · Merged option list. Falls back to the 7 built-in
    // taxonomy names if the dynamic /categories fetch has not yet
    // populated (first paint) or was denied. Custom-category names
    // returned by the endpoint are appended in server order.
    const options = (categories && categories.length > 0)
        ? categories.map((c) => c.name)
        : CATEGORY_OPTIONS;

    const save = async (nextValue) => {
        // `__review` sentinel = user chose to send product back to
        // Needs Review manually (category → null, source stays "manual").
        const body = nextValue === "__review"
            ? { set_category: true, category: null }
            : { set_category: true, category: nextValue };
        setSaving(true);
        try {
            const { data } = await api.patch(
                `/products/${product.id}`,
                body,
                { params },
            );
            toast.success(nextValue === "__review"
                ? t("inventory.markedNeedsReview", "Marked as Needs Review")
                : t("inventory.categorySet", { defaultValue: "Category set to {{name}}", name: nextValue }));
            onSaved?.(data);
        } catch (e) {
            toast.error(translateDetail(e) || t("inventory.couldNotUpdateCategory", "Could not update category"));
        } finally { setSaving(false); }
    };

    // "+ Add category" opens an inline dialog; anything else saves.
    const handleChange = (v) => {
        if (v === "__add") { setNewName(""); setAddOpen(true); return; }
        save(v);
    };

    const submitNewCategory = async () => {
        const name = newName.trim();
        if (!name) { toast.error(t("inventory2.categoryNameIsRequired", "Category name is required")); return; }
        setCreating(true);
        try {
            await api.post("/categories",
                { type: "product", name });
            toast.success(t("inventory.categoryCreated", { defaultValue: "Category \"{{name}}\" created", name }));
            setAddOpen(false);
            // Refresh the parent-owned merged list, then auto-select
            // the freshly created category on this product row.
            if (onCategoriesChanged) { await onCategoriesChanged(); }
            await save(name);
        } catch (e) {
            toast.error(translateDetail(e) || t("inventory.couldNotCreateCategory", "Could not create category"));
        } finally { setCreating(false); }
    };

    // Read-only badge for unauthorized users.
    if (!canEdit) {
        return cat
            ? <span data-testid={`product-category-${product.id}`}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-700">
                {catLabel(cat)}
              </span>
            : <span data-testid={`product-category-${product.id}`}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                {t("inventory.needsReview2", "Needs Review")}
              </span>;
    }

    return (
        <>
            <Select value={cat || ""} onValueChange={handleChange} disabled={saving}>
                <SelectTrigger
                    data-testid={`product-category-select-${product.id}`}
                    className={`h-8 w-[180px] text-xs ${cat ? "" : "border-red-300 text-red-700 bg-red-50"}`}>
                    <SelectValue placeholder={
                        <span data-testid={`product-category-needs-review-${product.id}`}
                              className="font-semibold">{t("inventory.needsReview2", "Needs Review")}</span>
                    }>
                        <span className="flex items-center gap-1.5">
                            {catLabel(cat)}
                            {src === "manual" && (
                                <span className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">
                                    manual
                                </span>
                            )}
                        </span>
                    </SelectValue>
                </SelectTrigger>
                <SelectContent>
                    {options.map((c) => (
                        <SelectItem key={c} value={c}
                            data-testid={`product-category-option-${product.id}-${c}`}>
                            {catLabel(c)}
                        </SelectItem>
                    ))}
                    <SelectItem value="__review"
                        data-testid={`product-category-option-${product.id}-review`}>
                        <span className="text-red-700">{t("inventory.needsReview2", "Needs Review")}…</span>
                    </SelectItem>
                    <SelectItem value="__add"
                        data-testid={`product-category-option-${product.id}-add`}>
                        <span className="text-neutral-900 font-semibold">+ Add category</span>
                    </SelectItem>
                </SelectContent>
            </Select>
            <Dialog open={addOpen} onOpenChange={(v) => !v && setAddOpen(false)}>
                <DialogContent className="max-w-md"
                    data-testid={`add-category-dialog-${product.id}`}>
                    <DialogHeader>
                        <DialogTitle>{t("inventory.addProductCategory", "Add product category")}</DialogTitle>
                        <DialogDescription>
                            {t("inventory.newCategoryHint", "The new category will be added to this list and selected for this product.")}
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">
                            {t("inventory.categoryName", "Category name")}
                        </label>
                        <Input
                            data-testid={`add-category-name-${product.id}`}
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && submitNewCategory()}
                            placeholder={t("inventory.eGCafS", "e.g. Cafés")}
                            className="mt-1"
                            autoFocus
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline"
                            onClick={() => setAddOpen(false)}
                            data-testid={`add-category-cancel-${product.id}`}>
                            {t("inventory.cancel", "Cancel")}
                        </Button>
                        <Button onClick={submitNewCategory} disabled={creating}
                            data-testid={`add-category-submit-${product.id}`}>
                            {creating ? t("inventory.creating", "Creating…") : t("inventory.createSelect", "Create & select")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}


function AddProductDialog({ open, onClose, onSaved }) {
    const { t } = useTranslation();
    const { withRestaurant } = useRestaurantScope();
    const [form, setForm] = useState({
        product_name: "", barcode: "", unit: "kpl", last_price: 0, vat: 14,
        category: "General", area: "Kitchen",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) setForm({
            product_name: "", barcode: "", unit: "kpl", last_price: 0, vat: 14,
            category: "General", area: "Kitchen",
        });
    }, [open]);

    const save = async () => {
        if (!form.product_name.trim()) { toast.error(t("inventory2.productNameRequired", "Product name required")); return; }
        setSaving(true);
        try {
            const body = withRestaurant({ ...form });
            await api.post("/products", body);
            toast.success(t("inventory2.productAdded", "Product added"));
            onSaved();
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent data-testid="add-product-dialog">
                <DialogHeader>
                    <DialogTitle>{t("inventory.newProduct", "New Product")}</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    {[
                        ["product_name", t("inventory.name", "Name"), "col-span-2", "text"],
                        ["barcode", t("inventory.barcode", "Barcode"), "", "text"],
                        ["unit", "Unit", "", "text"],
                        ["last_price", t("inventory.lastPrice", "Last price"), "", "number"],
                        ["vat", t("inventory.vat", "VAT %"), "", "number"],
                        ["category", t("inventory.category", "Category"), "", "text"],
                        ["area", "Area", "", "text"],
                    ].map(([k, label, cls, type]) => (
                        <div key={k} className={cls}>
                            <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{label}</label>
                            <input
                                data-testid={`product-${k}`}
                                type={type}
                                value={form[k]}
                                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md focus:outline-none focus:border-neutral-900"
                            />
                        </div>
                    ))}
                </div>
                <DialogFooter>
                    <button data-testid="dialog-cancel-button" onClick={onClose} className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold">{t("inventory.cancel", "Cancel")}</button>
                    <button data-testid="save-product-button" onClick={save} disabled={saving}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                        {saving ? t("inventory.saving", "Saving…") : t("inventory.save", "Save")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * PendingScansDrawer — Phase 2 · Durable Continuous Scanning.
 *
 * Lists every Companion barcode scan that could not be auto-added to the
 * active count (external / unknown match). Every entry stays visible
 * until the operator explicitly resolves or dismisses it — a new scan
 * NEVER overwrites an older unresolved one. Phone quantity is preserved
 * per row so the operator can honour it on resolution.
 */
function PendingScansDrawer({ open, events, onClose, onResolveSuggestion,
                              onResolveManual, onDismiss, onUseExisting, searchProducts }) {
    const { t } = useTranslation();
    const [pickFor, setPickFor] = useState(null);
    const [q, setQ] = useState("");
    const [hits, setHits] = useState([]);
    const runPick = async (val) => {
        setQ(val);
        setHits(val.trim() ? await searchProducts(val.trim()) : []);
    };
    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-lg" data-testid="pending-scans-drawer">
                <DialogHeader>
                    <DialogTitle>{t("inventory.pendingPhoneScans", "Pending phone scans")}</DialogTitle>
                    <DialogDescription>
                        {t("inventory.pendingScansDesc", "Scans received from the phone that could not be added automatically. Resolve each one below — new scans never overwrite these.")}
                    </DialogDescription>
                </DialogHeader>
                {events.length === 0 ? (
                    <div className="text-sm text-neutral-500 py-6 text-center">
                        {t("inventory.noPendingScans", "No pending scans.")}
                    </div>
                ) : (
                    <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                        {events.map((ev) => (
                            <div key={ev.id || ev.received_at}
                                 data-testid={`pending-scan-${ev.id}`}
                                 className="rounded-md border border-neutral-200 p-3 flex flex-wrap items-center gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs uppercase tracking-wide font-semibold text-neutral-500">
                                        {ev.match === "external" ? t("inventory.externalMatch", "External match")
                                            : ev.match === "ambiguous" ? t("inventory.ambiguousBarcode", "Barcode used by several products")
                                            : t("inventory.unknownBarcode", "Unknown barcode")}
                                    </div>
                                    <div className="text-sm font-mono truncate">{ev.barcode}</div>
                                    <div className="text-xs text-neutral-600">
                                        {t("inventory.qtyFromPhone", "Qty from phone:")} <span className="font-semibold">{ev.quantity}</span>
                                        {ev.suggestion?.product_name && (
                                            <> · Suggestion: {ev.suggestion.product_name}</>
                                        )}
                                    </div>
                                </div>
                                <div className="shrink-0 flex flex-col gap-1">
                                    {ev.match === "external" ? (
                                        <button
                                            data-testid={`pending-resolve-${ev.id}`}
                                            onClick={() => onResolveSuggestion(ev)}
                                            className="h-8 px-3 rounded-md bg-neutral-900 text-white text-xs font-semibold">
                                            {t("inventory.review", "Review")}
                                        </button>
                                    ) : (
                                        <button
                                            data-testid={`pending-manual-${ev.id}`}
                                            onClick={() => onResolveManual(ev)}
                                            className="h-8 px-3 rounded-md bg-neutral-900 text-white text-xs font-semibold">
                                            {t("inventory.create", "Create")}
                                        </button>
                                    )}
                                    <button
                                        data-testid={`pending-dismiss-${ev.id}`}
                                        onClick={() => onDismiss(ev)}
                                        className="h-8 px-3 rounded-md border border-neutral-300 text-neutral-700 text-xs font-semibold">
                                        {t("inventory.dismiss", "Dismiss")}
                                    </button>
                                    {ev.match !== "ambiguous" && (
                                        <button
                                            data-testid={`pending-existing-${ev.id}`}
                                            onClick={() => { setPickFor(pickFor === ev.id ? null : ev.id); setQ(""); setHits([]); }}
                                            className="h-8 px-3 rounded-md border border-neutral-300 text-neutral-700 text-xs font-semibold">
                                            {t("inventory.useExistingProduct", "Use existing product")}
                                        </button>
                                    )}
                                </div>
                                {pickFor === ev.id && (
                                    <div className="basis-full w-full" data-testid={`pending-existing-search-${ev.id}`}>
                                        <input value={q} onChange={(e) => runPick(e.target.value)} autoFocus
                                            placeholder={t("inventory.searchProductMaster", "Search Product Master…")}
                                            data-testid={`pending-existing-input-${ev.id}`}
                                            className="w-full h-9 px-3 rounded-md border border-neutral-300 text-sm" />
                                        <div className="mt-1 divide-y divide-neutral-100">
                                            {hits.map((p) => (
                                                <button key={p.id} onClick={() => { setPickFor(null); onUseExisting(ev, p); }}
                                                    data-testid={`pending-existing-pick-${p.id}`}
                                                    className="w-full text-left py-1.5 px-1 text-sm hover:bg-neutral-50 flex justify-between gap-2">
                                                    <span className="truncate">{p.product_name}</span>
                                                    <span className="text-xs text-neutral-500 shrink-0">{p.barcode || t("inventory.noBarcode", "No barcode")} · {p.unit}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}



// ─────────────────────────────────────────────────────────────
// Phase 5 (2026-02, Message 365) — Edit Product from Inventory
// (CATEGORY_OPTIONS is declared once at the top of this file — reused here.)
// ─────────────────────────────────────────────────────────────

function EditProductDialog({ open, product, countedItemId, countId, params,
                                onClose, onSaved }) {
    const { t } = useTranslation();
    const [category, setCategory] = useState("");
    const [name, setName] = useState("");
    const [unit, setUnit] = useState("");
    const [purchaseUnit, setPurchaseUnit] = useState("");
    const [packSize, setPackSize] = useState("");
    const [packSizeUnit, setPackSizeUnit] = useState("");
    const [lastPrice, setLastPrice] = useState("");
    const [lastPriceUnit, setLastPriceUnit] = useState("");
    const [saving, setSaving] = useState(false);

    // 2026-02 · Merged product-category list (seven built-ins + active
    // custom company categories), loaded via the same endpoint used by
    // the Products-table ProductCategoryCell. Fetched when the dialog
    // opens so the dropdown always reflects the current catalogue.
    const [categories, setCategories] = useState([]);
    const [addCatOpen, setAddCatOpen] = useState(false);
    const [newCatName, setNewCatName] = useState("");
    const [creatingCat, setCreatingCat] = useState(false);

    const loadCategories = async () => {
        try {
            const { data } = await api.get("/categories", {
                params: { type: "product", include_builtin: 1 },
            });
            setCategories(Array.isArray(data) ? data : []);
        } catch (_e) { /* Non-fatal: dropdown falls back to built-ins. */ }
    };

    // Active-list names from the merged endpoint. Falls back to the
    // seven built-in taxonomy names on first paint / fetch failure so
    // the dropdown is never empty.
    const activeNames = (categories && categories.length > 0)
        ? categories.map((c) => c.name)
        : CATEGORY_OPTIONS;

    // Message 385: legacy Product Master categories (e.g. "General")
    // that are not in the canonical taxonomy must NOT silently
    // masquerade as the t("inventory.needsReview", "Needs review") placeholder in the trigger. Show
    // the raw legacy value so the chef knows a canonical selection is
    // required. Preserve the already-assigned category — including an
    // inactive custom one — by appending it to the option list so the
    // Select can continue to display it until the user picks a new one.
    const isLegacyCategory = product && product.category
        && !activeNames.includes(product.category);

    const options = (product && product.category
        && !activeNames.includes(product.category))
        ? [...activeNames, product.category]
        : activeNames;

    useEffect(() => {
        if (!product) return;
        setCategory(product.category || "");
        setName(product.product_name || "");
        setUnit(product.unit || "");
        setPurchaseUnit(product.purchase_unit || "");
        setPackSize(product.pack_size ?? "");
        setPackSizeUnit(product.pack_size_unit || "");
        setLastPrice(product.last_price ?? "");
        setLastPriceUnit(product.last_price_unit || "");
    }, [product]);

    useEffect(() => { if (open) { loadCategories(); } }, [open]);

    // "+ Add category" sentinel opens the inline creation dialog; any
    // real option is applied to the local form state only. Save is
    // still owned by the main dialog Save button.
    const handleCategoryChange = (v) => {
        if (v === "__add__") { setNewCatName(""); setAddCatOpen(true); return; }
        setCategory(v === "__none__" ? "" : v);
    };

    const submitNewCategory = async () => {
        const nm = newCatName.trim();
        if (!nm) { toast.error(t("inventory2.categoryNameIsRequired", "Category name is required")); return; }
        setCreatingCat(true);
        try {
            await api.post("/categories", { type: "product", name: nm });
            toast.success(t("inventory.categoryCreated", { defaultValue: "Category \"{{name}}\" created", name: nm }));
            setAddCatOpen(false);
            await loadCategories();
            // Auto-select the freshly created category in the form —
            // the product is NOT saved here; the owner must still
            // press Save to persist the assignment.
            setCategory(nm);
        } catch (e) {
            // Reuses the existing create-category duplicate/error
            // handling (409 → "Category already exists").
            toast.error(translateDetail(e) || t("inventory.couldNotCreateCategory", "Could not create category"));
        } finally { setCreatingCat(false); }
    };

    // Normalized €/inventory-unit preview.
    const normalizedPricePreview = useMemo(() => {
        const p = Number(lastPrice);
        if (!p || p <= 0) return { ok: false, msg: t("inventory.missingValidPrice", "Missing valid price") };
        const pu = (lastPriceUnit || "").toUpperCase();
        const iu = (unit || "").toUpperCase();
        if (!iu) return { ok: false, msg: t("inventory.missingInventoryUnit", "Missing inventory unit") };
        if (pu === iu) return { ok: true, value: p, unit: iu };
        const ps = Number(packSize);
        const psu = (packSizeUnit || "").toUpperCase();
        if (!ps || !psu) return { ok: false, msg: t("inventory.missingPackSizeUnit", "Missing pack size / unit") };
        // Price is per PURCHASE-UNIT; pack maps 1 purchase-unit → ps
        // (in packSizeUnit). Only supports pack normalization when
        // packSizeUnit == inventory unit (the common KPL × KG case).
        if (psu !== iu) return { ok: false, msg: t("inventory.incompatibleUnits", "Incompatible units") };
        return { ok: true, value: p / ps, unit: iu };
    }, [lastPrice, lastPriceUnit, unit, packSize, packSizeUnit]);

    const save = async () => {
        if (!product?.id) return;
        setSaving(true);
        try {
            // Message 385: dirty-field PATCH payload. Only send fields
            // the owner actually changed. Prevents Save from silently:
            //  • re-stamping last_price_source = "manual" on an unchanged
            //    price (would clobber Delivery precedence signals).
            //  • bumping last_price_updated_at on a no-op Save.
            //  • re-writing product_name/unit/pack/etc that were not
            //    touched.
            // Category keeps its existing set_category intent flag; we
            // only send it when it changed.
            const initName = product.product_name || "";
            const initUnit = product.unit || "";
            const initPurchaseUnit = product.purchase_unit || "";
            const initPackSize = product.pack_size ?? "";
            const initPackSizeUnit = product.pack_size_unit || "";
            const initLastPrice = product.last_price ?? "";
            const initLastPriceUnit = product.last_price_unit || "";
            const initCategory = product.category || "";

            const body = {};
            if (category !== initCategory) {
                body.set_category = true;
                body.category = category || null;
            }
            if (name && name !== initName)
                body.product_name = name;
            if (unit && unit !== initUnit)
                body.unit = unit;
            if (purchaseUnit && purchaseUnit !== initPurchaseUnit)
                body.purchase_unit = purchaseUnit;
            if (packSize !== "" && String(packSize) !== String(initPackSize))
                body.pack_size = Number(packSize);
            if (packSizeUnit && packSizeUnit !== initPackSizeUnit)
                body.pack_size_unit = packSizeUnit;
            if (lastPrice !== "" && String(lastPrice) !== String(initLastPrice))
                body.last_price = Number(lastPrice);
            if (lastPriceUnit && lastPriceUnit !== initLastPriceUnit)
                body.last_price_unit = lastPriceUnit;

            // Nothing changed → skip network, still allow refresh-price
            // in case the chef opened the dialog just to trigger it.
            let updated = product;
            if (Object.keys(body).length > 0) {
                const resp = await api.patch(
                    `/products/${product.id}`,
                    body,
                    { params },
                );
                updated = resp.data;
            }
            // If we opened from a counted row, ask the OPEN inventory
            // to re-resolve the price. Historical counts are protected
            // server-side (completed → 409).
            let refreshFailure = null;
            if (countedItemId && countId) {
                try {
                    await api.post(`/inventory-counts/${countId}/items/${countedItemId}/refresh-price`);
                } catch (e) {
                    refreshFailure = translateErrorText(e.response?.data?.detail?.message
                        || e.response?.data?.detail)
                        || t("inventory.unknownError", "unknown error");
                }
            }
            // Message 385: toast honesty. Do NOT show a bare "Product
            // saved" success on a counted-row path if the refresh-price
            // step failed — the owner would reasonably conclude the
            // counted value updated when it did not.
            if (refreshFailure) {
                toast.error(
                    t("inventory.savedButRefreshFailed", { defaultValue: "Product saved, but the counted-row price could not be refreshed: {{reason}}", reason: refreshFailure }));
            } else {
                toast.success(t("inventory2.productSaved", "Product saved"));
            }
            await onSaved({ ...product, ...updated });
        } catch (e) {
            toast.error(translateDetail(e.response?.data?.detail)
                          || t("inventory.saveFailed2", "Save failed"));
        } finally { setSaving(false); }
    };

    if (!product) return null;
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl" data-testid="edit-product-dialog">
                <DialogHeader>
                    <DialogTitle>{t("inventory.editProduct", "Edit product")}</DialogTitle>
                    <DialogDescription>
                        {t("inventory.updateProductMasterHistoricalInventories", "Update Product Master. Historical inventories are not affected.")}
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("inventory.productName2", "Product name")}</label>
                        <input data-testid="edit-name" value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="mt-1 w-full h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                    </div>
                    <div className="col-span-2">
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("inventory.category", "Category")}</label>
                        <Select value={category || "__none__"}
                                onValueChange={handleCategoryChange}>
                            <SelectTrigger data-testid="edit-category" className="mt-1 h-10">
                                {isLegacyCategory && category === (product.category || "") ? (
                                    <span className="text-amber-700">
                                        {product.category} {t("inventory.legacySelectCategory", "(legacy — select category)")}
                                    </span>
                                ) : (
                                    <SelectValue placeholder={t("inventory.needsReview", "Needs review")} />
                                )}
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__none__">{t("inventory.needsReview", "Needs review")}</SelectItem>
                                {options.map(c => (
                                    <SelectItem key={c} value={c}
                                        data-testid={`edit-category-option-${c}`}>
                                        {catLabel(c)}
                                    </SelectItem>
                                ))}
                                <SelectItem value="__add__"
                                    data-testid="edit-category-option-add">
                                    <span className="text-neutral-900 font-semibold">+ Add category</span>
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("inventory.inventoryUnit", "Inventory unit")}</label>
                        <input data-testid="edit-unit" value={unit}
                            onChange={(e) => setUnit(e.target.value)}
                            className="mt-1 w-full h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("inventory.purchaseUnit", "Purchase unit")}</label>
                        <input data-testid="edit-purchase-unit" value={purchaseUnit}
                            onChange={(e) => setPurchaseUnit(e.target.value)}
                            className="mt-1 w-full h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("inventory.packSize", "Pack size")}</label>
                        <input data-testid="edit-pack-size" type="number" step="0.01" min="0" value={packSize}
                            onChange={(e) => setPackSize(e.target.value)}
                            className="mt-1 w-full h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("inventory.packSizeUnit", "Pack size unit")}</label>
                        <input data-testid="edit-pack-size-unit" value={packSizeUnit}
                            onChange={(e) => setPackSizeUnit(e.target.value)}
                            className="mt-1 w-full h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("inventory.lastPriceVat0", "Last price (VAT 0)")}</label>
                        <input data-testid="edit-last-price" type="number" step="0.0001" min="0" value={lastPrice}
                            onChange={(e) => setLastPrice(e.target.value)}
                            className="mt-1 w-full h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("inventory.lastPriceUnit", "Last price unit")}</label>
                        <input data-testid="edit-last-price-unit" value={lastPriceUnit}
                            onChange={(e) => setLastPriceUnit(e.target.value)}
                            className="mt-1 w-full h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                    </div>
                    <div className="col-span-2 text-xs text-neutral-600 bg-neutral-50 rounded-md p-2"
                            data-testid="normalized-price-preview">
                        {t("inventory.normalizedPrice", "Normalized inventory price:")} {normalizedPricePreview.ok
                            ? <b>€ {normalizedPricePreview.value.toFixed(4)} / {normalizedPricePreview.unit}</b>
                            : <span className="text-amber-700">{t("inventory.cannotCalculate", "Cannot calculate —")} {normalizedPricePreview.msg}</span>}
                    </div>
                    <div className="col-span-2 text-xs text-neutral-500">
                        VAT: <b>{product.vat ?? "—"}%</b> <span className="italic">(read-only here — edit VAT in Products page)</span>
                    </div>
                </div>
                <DialogFooter>
                    <button data-testid="edit-product-cancel" onClick={onClose}
                        className="h-10 px-4 rounded-md border border-neutral-300 text-sm font-semibold hover:bg-neutral-50">{t("inventory.cancel", "Cancel")}</button>
                    <button data-testid="edit-product-save" onClick={save} disabled={saving}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                        {saving ? t("inventory.saving", "Saving…") : t("inventory.save", "Save")}
                    </button>
                </DialogFooter>
            </DialogContent>
            {/* Add-category dialog: reuses the same creation API and
                duplicate/error handling as ProductCategoryCell. Cancel
                leaves the parent product form untouched. */}
            <Dialog open={addCatOpen} onOpenChange={(v) => !v && setAddCatOpen(false)}>
                <DialogContent className="max-w-md" data-testid="edit-add-category-dialog">
                    <DialogHeader>
                        <DialogTitle>{t("inventory.addProductCategory", "Add product category")}</DialogTitle>
                        <DialogDescription>
                            {t("inventory.newCategoryHintForm", "The new category will be added to the list and selected in this product form. Press Save to persist the assignment.")}
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">
                            {t("inventory.categoryName", "Category name")}
                        </label>
                        <Input
                            data-testid="edit-add-category-name"
                            value={newCatName}
                            onChange={(e) => setNewCatName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && submitNewCategory()}
                            placeholder={t("inventory.eGCafS", "e.g. Cafés")}
                            className="mt-1"
                            autoFocus
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline"
                            onClick={() => setAddCatOpen(false)}
                            data-testid="edit-add-category-cancel">
                            {t("inventory.cancel", "Cancel")}
                        </Button>
                        <Button onClick={submitNewCategory} disabled={creatingCat}
                            data-testid="edit-add-category-submit">
                            {creatingCat ? t("inventory.creating", "Creating…") : t("inventory.createSelect", "Create & select")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Dialog>
    );
}

// Message 460 · Section 4 — Edit Count quantity dialog.
// Edits the QUANTITY of an already-counted row. Preserves item_id,
// product_id, unit, and price. Backend recalculates total_value via
// the existing PATCH /inventory-counts/{cid}/items/{iid} contract.
// Explicit zero remains valid. Negative rejected. Completed counts
// still 400 on the server side.
function EditCountQuantityDialog({ item, countId, onClose, onSaved }) {
    const { t } = useTranslation();
    const [quantity, setQuantity] = useState("");
    const [saving, setSaving] = useState(false);

    const itemRef = useLatest(item);
    useEffect(() => {
        const it = itemRef.current;
        if (it) setQuantity(String(it.quantity ?? ""));
    }, [item?.id, itemRef]);

    const isOpen = !!item;
    const save = async () => {
        if (!item || !countId) return;
        const q = Number(quantity);
        if (!Number.isFinite(q) || q < 0) {
            toast.error(t("inventory2.quantityMustBeZeroOr", "Quantity must be zero or a positive number"));
            return;
        }
        setSaving(true);
        try {
            await api.patch(
                `/inventory-counts/${countId}/items/${item.id}`,
                { quantity: q, expected_quantity: item.quantity });
            toast.success(t("inventory.updatedQty", { defaultValue: "Updated: {{qty}} {{unit}} of {{name}}", qty: q, unit: item.unit || "", name: item.product_name }));
            if (onSaved) await onSaved();
        } catch (err) {
            const d = err.response?.data?.detail;
            if (d?.code === "quantity_changed" && onSaved) await onSaved();
            toast.error((d?.code ? translateErrorText(d.message) : translateDetail(d))
                || t("inventory.couldNotUpdateCount", "Could not update count"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="edit-count-qty-dialog"
                className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{t("inventory.editCount", "Edit count")}</DialogTitle>
                    <DialogDescription>
                        {item?.product_name}
                        <span className="text-neutral-500"> · unit: {item?.unit || "—"}</span>
                    </DialogDescription>
                </DialogHeader>
                <div className="mt-2">
                    <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">
                        Quantity (in <b>{item?.unit || "unit"}</b>)
                    </label>
                    <input
                        data-testid="edit-count-qty-input"
                        type="number" min="0" step="0.01" autoFocus
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && save()}
                        className="mt-1 w-full h-12 px-3 border border-neutral-300 rounded-md focus:outline-none focus:border-neutral-900 text-xl font-display font-bold"
                    />
                    <div className="mt-2 text-[11px] text-neutral-500">
                        {t("inventory.decimalsHint", { defaultValue: "Decimals allowed for {{unit}} (e.g. 0.1, 0.25, 0.75). Zero is a valid explicit count. Item ID and product ID are preserved.", unit: item?.unit || t("inventory.unitWord", "unit") })}
                    </div>
                </div>
                <DialogFooter>
                    <button data-testid="edit-count-qty-cancel" onClick={onClose}
                        className="h-10 px-4 rounded-md border border-neutral-300 text-sm font-semibold hover:bg-neutral-50">
                        {t("inventory.cancel", "Cancel")}
                    </button>
                    <button data-testid="edit-count-qty-save" onClick={save} disabled={saving}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                        {saving ? t("inventory.saving", "Saving…") : t("inventory.save", "Save")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

